import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import Grid from "@mui/material/Grid2";
import { Box, Button, Paper, Typography, Snackbar, Alert, TextField, Grid2, Stack } from "@mui/material";
import { Iconify } from "../../../Minimal/src/components/iconify/iconify";
import { ConfirmDialog } from "../../../common/confirm-dialog";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import InventorySearchModal from "../../QuickOrder/component/InventorySearchModal";
import CartTable from "../component/CartTableEposSales";
import CartSummary from "../../Epos/component/CartSummary";
import { fetchPlaceOrder, releasePlaceOrderLock, updateInvoice, checkProductPromotion, getProductsList, getCustomerList } from "../../../../data/epos";
import { PLATFORMS } from "../../../../data/cart";
import { getProformaInvoice } from "../../../../data/invoice";
import {
    getCartPromotions,
    collectProductIdsSharingPromotionWithLine,
    buildCartLinePromotionsFallback,
    resolveFreshCartLineForPromotionPopup,
    reconcilePromotionDisplayLists,
    resolveCartLineSlabId,
} from "../../../../data/promotions";
import { transformCartToPromotionPayload, mergePromotionResponseWithCart, enrichCartItemsWithProductCodes, formatDateForAPI, transformProductDetailsToCart, checkPromotionWithGetProductChanges, applyPromotionCheckTrigger, resolvePromotionCheckQueueMeta } from "../utils/promotionHelpers";
import { notifyPromotionMessage, PROMOTION_MESSAGE_TRIGGERS } from "../../../../utils/promotionMessageToast";
import { determineItemType, prepareProductForCart, parseQty, allowsDecimalQty, isMeatsProduct, resolveOrderTypeFromProduct, cartLineHasPromoOfferQtyControls, resolveProductTaxCode, computeTelecallerVatBreakdownTotals, getExVatGoodsValueForTotals, applyStickyBundleDiscountPolicy, createStickyBundleDiscountState, resetStickyBundleDiscountState, isRhpEposTenant, cartLineMissingDiscountComment } from "../../Epos/utils/cartCalculations";
import { isRhpPrintTenant } from "../../Epos/utils/rhpInvoicePrint";
import { getTelecallerMaxAvailableQty, shouldSkipTelecallerStockLimit, getTelecallerProductDisplayName } from "../../Epos/utils/telecallerStockLimit";
import { getStockCapForCartLineItem, isQuantityWithinLineStock } from "../../Promotion/utils/promotionLineStock";
import {
    syncCartQtyValidationMap,
    computeCartQuantityChange,
    applyCartQtyValidationResult,
    getCartLineValidationKey,
    getProductQtyTotalInCart,
} from "../../Epos/utils/cartQtyValidation";
import { useDebounce } from "minimal-shared/hooks";
import CartSummaryEposSales from "../../Epos/component/CartSummaryEposSales";
import CustomerSearch, { formatCustomerDisplayLabel } from "../../Epos/component/CustomerSearch";
import ProductSearch from "../../Epos/component/ProductSearch";
import config, { retainsCartDiscountFocus } from "../../../common/Config";
import { buildProductSearchIndexUrl } from "../../../../utils/productSearchUrls";
import { searchTokensFromQuery, productMatchesSearchTokens, sortProductsBySearchRelevance, hasSearchableProductQuery } from "../../../../productSearch/matchProductTokens";
import { resolveCheckStockBeforeAddToCart, resolveIsPriceEditable } from "../../../../utils/companySettingsCache";
import { useCompanySettings } from "../../../../contexts/CompanySettingsContext";
import PromotionDetailsDrawerEditEpos from "../../Promotion/components/promo-details-drawer-edit-epos";
import PromotionGetProductsDialog from "../../../common/PromotionGetProductsDialog";
import SalesStockLimitDialog from "../../common/SalesStockLimitDialog";
import {
    validateMaximumOrderQuantity,
    validateMaximumOrderQuantityForCartAdd,
    validateMaximumOrderQuantityForLineQty,
    mergeOrderLimitFieldsFromCartLines,
    EMPTY_SALES_LIMIT_DIALOG,
    buildStockLimitDialogState,
    buildPurchaseLimitDialogState,
} from "../../common/maximumOrderQuantity";
import "../../../common/commonStyles.css";
import PromotionsDialog from "../component/PromotionsDialog";
import ProductLinePromotionsDialog from "../../../common/ProductLinePromotionsDialog";
import BarcodeScanner from "../../../common/BarcodeScanner";
import NumericKeypad from "../../../common/NumericKeypad";
import { isInsideModalDialog } from "../../Epos/utils/cartTableKeyboard";
import { isCartLineOfferDisplayRole, isSameCartPaidOfferLine, findMergeableCartLineForProductAdd } from "../../Promotion/utils/promotionOfferProductsShared";

const normalizePayloadOrderType = (t) => String(t || "box").trim().toLowerCase();

const isSyntheticCartLineId = (id) => {
    const s = String(id ?? "").trim();
    if (!s) return false;
    return s.startsWith("promo_gen_") || s.startsWith("ed_promo_");
};

const promoRoleKeyForOriginalLine = (entity) => {
    if (!entity) return "standard";
    if (isCartLineOfferDisplayRole(entity)) return "get_or_free";
    const pt = String(
        entity.promoType ??
            entity.promosType ??
            entity.promo_type ??
            entity.promos_type ??
            entity.buyOrGet ??
            ""
    )
        .trim()
        .toUpperCase();
    if (pt === "BUY") return "buy_promo";
    return "standard";
};

const isCartLineMergeableOnProductAdd = (item) =>
    promoRoleKeyForOriginalLine(item) !== "get_or_free";

const promoIdBucket = (entity) => {
    const raw =
        entity?.promoId ??
        entity?.promosId ??
        entity?.promo_id ??
        entity?.promos_id ??
        entity?.promotion_id ??
        0;
    const n = parseInt(String(raw), 10);
    return Number.isFinite(n) ? n : 0;
};

/**
 * Picks the correct row from `product_details` for each cart line so duplicate products
 * (e.g. paid line + GET/free line) send distinct `id` values in ws_update_invoice_new.
 */
const resolveOriginalOrderLineForInvoicePayload = (
    originalOrderProducts,
    item,
    productId,
    orderTypeNorm,
    usedOriginalLineIds
) => {
    const list = Array.isArray(originalOrderProducts) ? originalOrderProducts : [];
    if (!productId || list.length === 0) return null;

    const markUsed = (orig) => {
        if (orig && orig.id !== undefined && orig.id !== null && String(orig.id).trim() !== "") {
            usedOriginalLineIds.add(String(orig.id));
        }
        return orig;
    };

    const sameProductAndPiece = list.filter(
        (p) =>
            String(p.product_id ?? "") === String(productId) &&
            normalizePayloadOrderType(p.order_type || p.type) === orderTypeNorm
    );

    const itemIdStr =
        item?.id !== undefined && item?.id !== null ? String(item.id).trim() : "";
    if (itemIdStr && !isSyntheticCartLineId(itemIdStr)) {
        const byExplicitId = list.find((p) => String(p.id) === itemIdStr);
        if (byExplicitId && String(byExplicitId.product_id ?? "") === String(productId)) {
            if (!usedOriginalLineIds.has(String(byExplicitId.id))) {
                return markUsed(byExplicitId);
            }
        }
    }

    const role = promoRoleKeyForOriginalLine(item);
    const itemPromoBucket = promoIdBucket(item);

    let pool = sameProductAndPiece.filter((p) => !usedOriginalLineIds.has(String(p.id)));

    const roleMatches = pool.filter((p) => promoRoleKeyForOriginalLine(p) === role);
    if (roleMatches.length > 0) {
        pool = roleMatches;
    }

    if (itemPromoBucket > 0) {
        const withPromo = pool.filter((p) => promoIdBucket(p) === itemPromoBucket);
        if (withPromo.length > 0) {
            pool = withPromo;
        }
    }

    pool.sort((a, b) => {
        const na = parseInt(String(a.id), 10);
        const nb = parseInt(String(b.id), 10);
        if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) {
            return na - nb;
        }
        return String(a.id).localeCompare(String(b.id));
    });

    const chosen = pool[0] ?? null;
    return chosen ? markUsed(chosen) : null;
};

const resolveProductWiseDiscountFromPromotionApi = (item, baseTotal) => {
    if (!item.useBackendCalculations) {
        return null;
    }

    const apiType = item.api_productWiseDiscountType;
    const apiValue = item.api_productWiseDiscountValue;
    const apiPct = item.api_productWiseDiscountPercentage;
    const apiAmt = item.api_productWiseDiscountAmount;

    const hasApiType = apiType !== undefined && apiType !== null && String(apiType).trim() !== "";
    const hasApiValue = apiValue !== undefined && apiValue !== null && String(apiValue).trim() !== "";
    const hasApiPct = apiPct !== undefined && apiPct !== null && Number.isFinite(parseFloat(apiPct));
    const hasApiAmt = apiAmt !== undefined && apiAmt !== null && Number.isFinite(parseFloat(apiAmt));

    if (!hasApiType && !hasApiValue && !hasApiPct && !hasApiAmt) {
        // No productWiseDiscount* fields from check-promotion — fall back to
        // item_discount_type / discount_type so ws_update_invoice_new matches the cart.
        return null;
    }

    const fallbackType =
        item.discount_type ||
        item.productWiseDiscountType ||
        item.item_discount_type ||
        "";
    const normalizedType = hasApiType
        ? String(apiType).trim().toLowerCase()
        : (fallbackType
            ? String(fallbackType).trim().toLowerCase()
            : (hasApiPct && !hasApiAmt && !hasApiValue ? "percentage" : "amount"));
    const isAmountType = normalizedType === "amount" || normalizedType === "fixed" || normalizedType === "£";

    let amount = 0;
    let percentage = 0;

    if (hasApiAmt) {
        amount = parseFloat(apiAmt) || 0;
    } else if (hasApiValue && isAmountType) {
        amount = parseFloat(apiValue) || 0;
    }

    if (hasApiPct) {
        percentage = parseFloat(apiPct) || 0;
    } else if (hasApiValue && !isAmountType) {
        percentage = parseFloat(apiValue) || 0;
    }

    if (isAmountType) {
        if (!hasApiPct && baseTotal > 0 && amount > 0) {
            percentage = (amount / baseTotal) * 100;
        }
    } else if (!hasApiAmt && percentage > 0) {
        amount = baseTotal * (percentage / 100);
    }

    return {
        productWiseDiscountType: isAmountType ? "amount" : "percentage",
        productWiseDiscountPercentage: percentage,
        productWiseDiscountAmount: amount,
    };
};

const isFreeProductLineForInvoice = (item) => {
    const promoTypeNorm = String(
        item.promo_type ?? item.promoType ?? item.promosType ?? item.buyOrGet ?? ""
    ).trim().toUpperCase();
    if (promoTypeNorm === "FREE") return true;
    if (String(item.free_product ?? "").trim().toUpperCase() === "Y") return true;
    const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;
    const promoUnitPrice = item.promo_unit_price !== undefined && item.promo_unit_price !== null && item.promo_unit_price !== ""
        ? parseFloat(item.promo_unit_price)
        : NaN;
    if (isPromoted && Number.isFinite(promoUnitPrice) && promoUnitPrice === 0) return true;
    const discountPrice = parseFloat(item.discount_price ?? NaN);
    if (isPromoted && Number.isFinite(discountPrice) && discountPrice === 0) return true;
    return false;
};

const resolveInvoicePayloadUnitPrice = (item) => {
    if (isFreeProductLineForInvoice(item)) {
        return 0;
    }
    const isPromoted =
        item.is_promoted === "1" ||
        item.is_promoted === 1 ||
        item.promotion_applied === true ||
        item.promotion_applied === "1" ||
        item.promotion_applied === 1;
    const promoUnitPrice =
        item.promo_unit_price !== undefined &&
        item.promo_unit_price !== null &&
        item.promo_unit_price !== ""
            ? parseFloat(item.promo_unit_price)
            : NaN;
    if (isPromoted && Number.isFinite(promoUnitPrice) && promoUnitPrice > 0.0001) {
        return promoUnitPrice;
    }
    const unitPrice = parseFloat(item.unit_price ?? item.price ?? 0);
    if (Number.isFinite(unitPrice) && unitPrice > 0.0001) {
        return unitPrice;
    }
    if (isPromoted && Number.isFinite(promoUnitPrice) && promoUnitPrice >= 0) {
        return promoUnitPrice;
    }
    return Number.isFinite(unitPrice) ? unitPrice : 0;
};

const EditEposSales = () => {
    const { orderId } = useParams();
    const navigate = useNavigate();

    const orderIdForLockRef = useRef(orderId);
    const lockReleasedRef = useRef(false);
    orderIdForLockRef.current = orderId;

    const releaseLock = useCallback((keepalive) => {
        const id = orderIdForLockRef.current;
        if (!id || lockReleasedRef.current) return;
        lockReleasedRef.current = true;
        releasePlaceOrderLock(String(id), { keepalive });
    }, []);

    useEffect(() => {
        lockReleasedRef.current = false;
    }, [orderId]);

    useEffect(() => {
        const role = String(localStorage.getItem("user_role") || "").trim();
        if (isRhpEposTenant() && role === "18") {
            navigate('/sales/epos_sales', { replace: true });
        }
    }, [navigate]);

    useEffect(() => {
        lastValidAllotQtyRef.current = new Map();
        stockLimitRevertRef.current = null;
    }, [orderId]);

    useEffect(() => {
        if (!orderId) return undefined;
        const onPageHide = (event) => {
            if (event.persisted) return;
            releaseLock(true);
        };
        window.addEventListener("pagehide", onPageHide);
        return () => {
            window.removeEventListener("pagehide", onPageHide);
            releaseLock(false);
        };
    }, [orderId, releaseLock]);

    useEffect(() => {
        if (!orderId) return undefined;
        window.history.pushState(null, "", window.location.href);
        const onPopState = () => {
            const ok = window.confirm(
                "Leave this order? The order will unlock for other users if you leave."
            );
            if (ok) {
                window.removeEventListener("popstate", onPopState);
                releaseLock(false);
                navigate(-1);
            } else {
                window.history.pushState(null, "", window.location.href);
            }
        };
        window.addEventListener("popstate", onPopState);
        return () => {
            window.removeEventListener("popstate", onPopState);
        };
    }, [orderId, navigate, releaseLock]);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [orderHeader, setOrderHeader] = useState(null);
    const [orderChannel, setOrderChannel] = useState("pos");
    const [totalInvDiscPercent, setTotalInvDiscPercent] = useState(0);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [staffNote, setStaffNote] = useState("");
    const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
    const [inventoryModalAnchorPosition, setInventoryModalAnchorPosition] = useState(null);
    const [productSearchInput, setProductSearchInput] = useState("");
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [quantityKeypadOpen, setQuantityKeypadOpen] = useState(false);
    const [selectedProductForQuantity, setSelectedProductForQuantity] = useState(null);
    const [keypadError, setKeypadError] = useState("");
    const [keypadInitialOrderType, setKeypadInitialOrderType] = useState("Box");
    const [customerSearchInput, setCustomerSearchInput] = useState("");
    const [saveConfirmDialog, setSaveConfirmDialog] = useState(false);
    const [cancelConfirmDialog, setCancelConfirmDialog] = useState(false);
    const [saveAndInvoiceConfirmDialog, setSaveAndInvoiceConfirmDialog] = useState(false);
    const [deleteItemDialog, setDeleteItemDialog] = useState({ open: false, itemId: null });
    const [telecallerStockDialog, setTelecallerStockDialog] = useState(EMPTY_SALES_LIMIT_DIALOG);
    const [newlyAddedProductId, setNewlyAddedProductId] = useState(null);
    const [promotionsModalOpen, setPromotionsModalOpen] = useState(false);
    const [productLinePromotionsCtx, setProductLinePromotionsCtx] = useState(null);
    const [promotionDrawerOpen, setPromotionDrawerOpen] = useState(false);
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [promoProductSwapOpen, setPromoProductSwapOpen] = useState(false);
    const [promoProductSwapCtx, setPromoProductSwapCtx] = useState(null);
    const [promotionRefreshKey, setPromotionRefreshKey] = useState(0);
    const [cartItems, setCartItems] = useState([]);
    const cartItemsRef = useRef([]);
    const cartQtyValidationRef = useRef(new Map());
    const lastValidAllotQtyRef = useRef(new Map());
    const stockLimitRevertRef = useRef(null);
    const promotionCheckEpochRef = useRef(0);
    const [originalOrderProducts, setOriginalOrderProducts] = useState([]);
    const [customerId, setCustomerId] = useState(null);
    const [promotionCheckQueue, setPromotionCheckQueue] = useState({});
    const promotionCheckTimerRef = useRef(null);
    const promotionCheckQueueRef = useRef({});
    const promotionCheckTriggerRef = useRef({ trigger: null, changedProductId: null });

    const enqueuePromotionCheck = useCallback((productId, trigger = null) => {
        const pid = productId != null && String(productId).trim() !== ""
            ? String(productId)
            : `_anon_${Date.now()}`;
        const nextTrigger = trigger || null;
        const prevMeta = promotionCheckTriggerRef.current || {};
        if (nextTrigger === PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT) {
            promotionCheckTriggerRef.current = {
                trigger: PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT,
                changedProductId: pid.startsWith("_anon_") ? prevMeta.changedProductId : pid,
            };
        } else if (
            nextTrigger === PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE &&
            prevMeta.trigger !== PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT
        ) {
            promotionCheckTriggerRef.current = {
                trigger: PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE,
                changedProductId: pid.startsWith("_anon_") ? prevMeta.changedProductId : pid,
            };
        } else if (
            nextTrigger &&
            !prevMeta.trigger
        ) {
            promotionCheckTriggerRef.current = {
                trigger: nextTrigger,
                changedProductId: pid.startsWith("_anon_") ? null : pid,
            };
        } else if (
            !nextTrigger &&
            !prevMeta.trigger &&
            !pid.startsWith("_anon_")
        ) {
            promotionCheckTriggerRef.current = {
                trigger: null,
                changedProductId: pid,
            };
        }

        setPromotionCheckQueue((prev) => {
            const next = {
                ...prev,
                [pid]: { t: Date.now(), trigger: nextTrigger },
            };
            promotionCheckQueueRef.current = next;
            return next;
        });
    }, []);

    const [promotionSummary, setPromotionSummary] = useState(null);
    const [cartTotals, setCartTotals] = useState({
        boxCount: 0,
        pieceCount: 0,
        subtotal: 0,
        vat: 0,
        stdGoods: 0,
        zeroGoods: 0,
        totalBeforeDiscount: 0,
        discountAmount: 0,
        promoDiscPercent: 0,
        promoDisc: 0,
        promoVat: 0,
        vatExcludingPromo: 0,
        grandTotal: 0,
        gross: 0
    });
    const [apiTotals, setApiTotals] = useState(null);
    const [bundleDiscounts, setBundleDiscounts] = useState([]);
    const allotQtyDebounceTimerRef = useRef(null);
    const lastDiscountCalculationRef = useRef({ totalDiscountAmount: 0, itemsHash: "" });
    const customerSearchRef = useRef(null);
    const productSearchRef = useRef(null);
    const telecallerProductSearchRef = useRef(null);
    const isBarcodeProcessingRef = useRef(false);
    const isQuantityPopupOpenRef = useRef(false);
    const debouncedProductSearch = useDebounce(productSearchInput, 500);
    const debouncedCustomerSearch = useDebounce(customerSearchInput, 500);
    const tillUserId = localStorage.getItem('tillUserId');

    const isTelecaller = useMemo(() => {
        return localStorage.getItem("user_role") === "20";
    }, []);

    const stickyBundleStateRef = useRef(null);
    if (!stickyBundleStateRef.current) {
        stickyBundleStateRef.current = createStickyBundleDiscountState();
    }

    const { effectiveBundleDiscounts, bundleLineDiscountByKey } = useMemo(() => {
        if (!isTelecaller || !Array.isArray(bundleDiscounts) || bundleDiscounts.length === 0) {
            return {
                effectiveBundleDiscounts: bundleDiscounts,
                bundleLineDiscountByKey: null,
            };
        }

        const stickyResult = applyStickyBundleDiscountPolicy(
            bundleDiscounts,
            cartItems,
            stickyBundleStateRef.current
        );

        return {
            effectiveBundleDiscounts: stickyResult.bundleDiscounts,
            bundleLineDiscountByKey: stickyResult.lineDiscountByKey,
        };
    }, [bundleDiscounts, cartItems, isTelecaller]);

    const isRhp = useMemo(() => {
        const accessToken = localStorage.getItem('accessToken');
        return accessToken === 'rhp' || accessToken === 'hp';
    }, []);

    const isTelecallerRhpOfferPriceLocked = isTelecaller && isRhp;

    const productSearchApiBase = useMemo(() => {
        const indexUrl = buildProductSearchIndexUrl(config.API_URL);
        return indexUrl.replace(/\/api\/products\/search-index\/?$/i, "");
    }, []);

    const { settings: companySettings, loaded: companySettingsLoaded } = useCompanySettings();
    const isPriceEditableFromSettings = resolveIsPriceEditable(companySettings);
    const shouldCheckStockBeforeAddToCart = resolveCheckStockBeforeAddToCart(companySettings);
    const isPriceEditableFromSettingsLoaded = companySettingsLoaded;

    const queryClient = useQueryClient();
    const editEposOrderQueryKey = useMemo(() => ["editEposOrder", orderId], [orderId]);

    const { data: orderData, isLoading, isError } = useQuery({
        queryKey: editEposOrderQueryKey,
        queryFn: () => fetchPlaceOrder(orderId),
        enabled: !!orderId,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 30,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const { data: customerList, isLoading: isLoadingCustomerList } = useQuery({
        queryKey: ["customerList", debouncedCustomerSearch, tillUserId],
        queryFn: () => getCustomerList({ pattern: debouncedCustomerSearch }),
        enabled: debouncedCustomerSearch.length >= 3,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
    });

    const { data: productListTelecaller, isLoading: isLoadingProductListTelecaller } = useQuery({
        queryKey: ["productList", "searchIndexLocalV3", selectedCustomer?.id, tillUserId],
        queryFn: () => getProductsList({
            pattern: "",
            customer_id: selectedCustomer?.id,
            platform: PLATFORMS.TELE_CALLING,
            apiBaseUrl: productSearchApiBase,
            useSearchIndex: true,
            returnAllIndex: true,
        }),
        enabled: !!isTelecaller && !!selectedCustomer?.id,
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
    });

    const { data: productListPos, isLoading: isLoadingProductListPos } = useQuery({
        queryKey: ["productList", debouncedProductSearch, selectedCustomer?.id, tillUserId, orderChannel],
        queryFn: () => getProductsList({
            pattern: debouncedProductSearch,
            customer_id: selectedCustomer?.id,
            orderChannel: orderChannel
        }),
        enabled: !isTelecaller && !!selectedCustomer?.id && hasSearchableProductQuery(debouncedProductSearch),
        staleTime: 1000 * 60 * 5,
        gcTime: 1000 * 60 * 10,
        refetchOnWindowFocus: false,
    });

    const activeProductList = isTelecaller ? productListTelecaller : productListPos;
    const isLoadingProductList = isTelecaller ? isLoadingProductListTelecaller : isLoadingProductListPos;

    const productOptionsRaw = useMemo(() => {
        const pl = activeProductList;
        return (
            pl?.product_details ||
            pl?.product_detials ||
            pl?.data?.product_details ||
            pl?.data?.product_detials ||
            []
        );
    }, [activeProductList]);

    const productOptions = useMemo(() => {
        if (!isTelecaller) {
            return productOptionsRaw;
        }
        const tokens = searchTokensFromQuery(productSearchInput);
        if (tokens.length === 0) return [];
        return sortProductsBySearchRelevance(
            productOptionsRaw.filter((item) => productMatchesSearchTokens(item, tokens)),
            tokens
        );
    }, [isTelecaller, productOptionsRaw, productSearchInput]);

    const promotionChannel = useMemo(() => {
        const userRole = localStorage.getItem("user_role");
        if (userRole === "20") {
            return "5";
        }
        if (orderHeader?.channel != null && orderHeader?.channel !== "") {
            return String(orderHeader.channel);
        }
        if (userRole === "18") {
            return "4";
        }
        return orderChannel || "pos";
    }, [orderChannel, orderHeader]);

    const checkPromotionMutation = useMutation({
        mutationFn: async (payload) => {
            const epochAtStart = promotionCheckEpochRef.current;
            const data = await checkProductPromotion(payload);
            return { data, epochAtStart, trigger: payload?.trigger ?? null };
        },
        onSuccess: (result) => {
            const data = result?.data;
            const epochAtStart = result?.epochAtStart;
            if (epochAtStart !== promotionCheckEpochRef.current) {
                return;
            }
            if (data?.success === false || localStorage.getItem("user_role") === "20") {
                notifyPromotionMessage(
                    data,
                    data?.success === false ? {} : { trigger: result?.trigger ?? null }
                );
            }
            if (Array.isArray(data?.data?.items)) {
                const currentCartItems = cartItemsRef.current;
                const cartDataForMerge = {
                    data: currentCartItems.length > 0
                        ? currentCartItems
                        : transformProductDetailsToCart(originalOrderProducts),
                    success: true
                };
                let mergedItems = mergePromotionResponseWithCart(cartDataForMerge, data, originalOrderProducts, currentCartItems);
                mergedItems = enrichCartItemsWithProductCodes(mergedItems, productOptionsRaw);
                cartItemsRef.current = mergedItems;
                setCartItems(mergedItems);
                lastValidAllotQtyRef.current = syncCartQtyValidationMap(mergedItems, {
                    qtyField: "allot",
                });

                if (data?.data?.totals) {
                    setApiTotals(data.data.totals);
                }

                if (Array.isArray(data?.data?.bundle_discounts)) {
                    setBundleDiscounts(data.data.bundle_discounts);
                } else {
                    setBundleDiscounts([]);
                }

                if (data?.data?.promotion_summary) {
                    setPromotionSummary(data.data.promotion_summary);
                } else if (data?.data?.applied_promotions && Array.isArray(data.data.applied_promotions)) {
                    setPromotionSummary({
                        applied_promotions: data.data.applied_promotions,
                        available_promotions: data.data.available_promotions || data.data.applicable_promotions || []
                    });
                } else {
                    setPromotionSummary(null);
                }
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
        },
    });

    const updateInvoiceMutation = useMutation({
        mutationFn: updateInvoice,
        onSuccess: (data) => {
            setSaveConfirmDialog(false);
            if (data?.success) {
                setSnackbar({ open: true, message: "Invoice updated successfully", severity: "success" });
                setTimeout(() => {
                    navigate('/sales/epos_sales');

                }, 1000);
            } else {
                setSnackbar({ open: true, message: data?.message || "Failed to update invoice", severity: "error" });
            }
        },
        onError: (error) => {
            setSnackbar({ open: true, message: error.message || "Failed to update invoice", severity: "error" });
        },
    });

    const getItemDetails = useCallback((item) => {
        return determineItemType(item);
    }, []);

    const calculateProportionalInvoiceDiscount = useCallback((items, totalDiscountAmount) => {
        if (!items || items.length === 0 || totalDiscountAmount === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        let totalContribution = 0;
        const itemContributions = items.map((item) => {
            const price = parseFloat(item.price) || 0;
            const qty = parseInt(item.qty) || 0;
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const itemDiscountType = item.discount_type || item.productWiseDiscountType || "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let baseTotal = price * qty;

            let discountedTotal;
            if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
            }

            let subtotal;
            let vat;
            if (isVatInclusive && vatRate > 0) {
                subtotal = discountedTotal / (1 + vatRate / 100);
                vat = discountedTotal - subtotal;
            } else {
                subtotal = discountedTotal;
                vat = subtotal > 0 ? (subtotal * vatRate) / 100 : 0;
            }

            const itemTotalBeforeInvoiceDiscount = subtotal + vat;
            totalContribution += itemTotalBeforeInvoiceDiscount;

            return {
                itemId: item.id || item.product_id,
                contribution: itemTotalBeforeInvoiceDiscount
            };
        });

        if (totalContribution === 0) {
            return items.map(item => ({ ...item, invoiceDiscountAmount: 0 }));
        }

        return items.map((item) => {
            const contributionData = itemContributions.find(c =>
                c.itemId === (item.id || item.product_id)
            );

            if (!contributionData) {
                return { ...item, invoiceDiscountAmount: 0 };
            }

            const contributionRatio = contributionData.contribution / totalContribution;
            const proportionalDiscountAmount = totalDiscountAmount * contributionRatio;

            return {
                ...item,
                invoiceDiscountAmount: proportionalDiscountAmount
            };
        });
    }, []);

    const calculateCartTotals = useCallback(() => {
        const bundleList = Array.isArray(effectiveBundleDiscounts) ? effectiveBundleDiscounts : [];

        if (isTelecaller && cartItems && cartItems.length > 0) {
            let boxCount = 0;
            let pieceCount = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseInt(item.qty) || 0;
                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }
            });

            const telecallerTotals = computeTelecallerVatBreakdownTotals(cartItems, bundleList);

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: telecallerTotals.stdGoods + telecallerTotals.zeroGoods,
                vat: telecallerTotals.vat,
                stdGoods: telecallerTotals.stdGoods,
                zeroGoods: telecallerTotals.zeroGoods,
                totalBeforeDiscount: telecallerTotals.grandTotal,
                discountAmount: 0,
                promoDiscPercent: 0,
                promoDisc: 0,
                promoVat: 0,
                vatExcludingPromo: telecallerTotals.vat,
                grandTotal: telecallerTotals.grandTotal,
                gross: 0,
            });
            return;
        }

        let bundlePromoDiscount = 0;
        let bundlePromoVat = 0;
        if (cartItems && cartItems.length > 0) {
            bundleList.forEach((b) => {
                bundlePromoDiscount += parseFloat(b.buy_total_discount || 0) + parseFloat(b.get_total_discount || 0);
                bundlePromoVat += parseFloat(b.buy_discount_vat || 0) + parseFloat(b.get_discount_vat || 0);
            });
        }

        if (apiTotals && cartItems.length > 0 && cartItems.some(item => item.useBackendCalculations)) {
            let boxCount = 0;
            let pieceCount = 0;
            let stdGoods = 0;
            let zeroGoods = 0;
            let freePromoLineVatSum = 0;

            cartItems.forEach((item) => {
                const orderType = item.type || item.order_type || "Box";
                const qty = parseInt(item.qty) || 0;

                if (orderType === "Box") {
                    boxCount += qty;
                } else {
                    pieceCount += qty;
                }

                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const itemSubtotalForGoods = getExVatGoodsValueForTotals(item);

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";
                if (isPromoFreeLine) {
                    const lv = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                    if (Number.isFinite(lv)) {
                        freePromoLineVatSum += lv;
                    }
                }
                if (!isPromoFreeLine && itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            });

            const backendSubtotal = parseFloat(apiTotals.subtotal || apiTotals.sub_total || 0);
            const backendVatRaw = parseFloat(apiTotals.vat || apiTotals.tax || 0);
            const backendVat = Math.max(0, backendVatRaw - (Number.isFinite(freePromoLineVatSum) ? freePromoLineVatSum : 0));
            const backendTotalBeforeDiscount = parseFloat(apiTotals.total || apiTotals.total_before_discount || backendSubtotal + backendVat);
            const gross = parseFloat((stdGoods + zeroGoods + backendVat).toFixed(2));

            const totalInvoiceDiscountAmount = (gross * totalInvDiscPercent) / 100;
            const hasBundlePromoVat = Number.isFinite(bundlePromoVat) && bundlePromoVat > 0.0001;
            const vatExcludingPromo = hasBundlePromoVat
                ? parseFloat((backendVat - bundlePromoVat).toFixed(2))
                : 0;
            const promoVatAdjust = hasBundlePromoVat ? bundlePromoVat : 0;
            const localGrand = Math.max(0, gross - bundlePromoDiscount - promoVatAdjust);
            const apiGrandParsed = parseFloat(apiTotals.grand_total ?? apiTotals.grandTotal ?? NaN);
            const hasLineItemDiscount = cartItems.some((it) => {
                const d = parseFloat(it.discount ?? it.item_discount ?? it.manualOverrides?.discount ?? 0);
                return Number.isFinite(d) && d > 0.0001;
            });
            const grandTotal = hasLineItemDiscount
                ? localGrand
                : Number.isFinite(apiGrandParsed)
                    ? Math.max(0, apiGrandParsed)
                    : localGrand;

            setCartTotals({
                boxCount,
                pieceCount,
                subtotal: backendSubtotal,
                vat: backendVat,
                stdGoods,
                zeroGoods,
                totalBeforeDiscount: gross,
                discountAmount: totalInvoiceDiscountAmount,
                promoDiscPercent: 0,
                promoDisc: bundlePromoDiscount,
                promoVat: bundlePromoVat,
                vatExcludingPromo,
                grandTotal,
                gross
            });
            return;
        }

        let boxCount = 0;
        let pieceCount = 0;
        let stdGoods = 0;
        let zeroGoods = 0;
        let subtotalAmount = 0;
        let vatAmount = 0;

        cartItems.forEach((item) => {
            const orderType = item.type || item.order_type || "Box";
            const qty = parseInt(item.qty) || 0;

            if (orderType === "Box") {
                boxCount += qty;
            } else {
                pieceCount += qty;
            }

            if (item.useBackendCalculations) {
                const lineVat = parseFloat(item.line_vat || item.line_tax || item.vat || item.tax || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const itemSubtotalForGoods = getExVatGoodsValueForTotals(item);

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";

                if (!isPromoFreeLine) {
                    subtotalAmount += itemSubtotalForGoods;
                    vatAmount += itemSubtotalForGoods > 0.0001 ? lineVat : 0;
                } else {
                    subtotalAmount += itemSubtotalForGoods;
                }

                if (!isPromoFreeLine && itemSubtotalForGoods > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotalForGoods;
                    } else {
                        zeroGoods += itemSubtotalForGoods;
                    }
                }
            } else {
                const price = parseFloat(item.price) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                const promoTypeNorm = String(item.promo_type || item.promoType || "").trim().toUpperCase();
                const isPromoFreeLine = promoTypeNorm === "FREE";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                if (isPromoFreeLine) {
                    itemVat = 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;

                if (!isPromoFreeLine && itemSubtotal > 0) {
                    if (vatRate > 0) {
                        stdGoods += itemSubtotal;
                    } else {
                        zeroGoods += itemSubtotal;
                    }
                }
            }
        });

        const gross = parseFloat((stdGoods + zeroGoods + vatAmount).toFixed(2));
        const totalBeforeDiscount = gross;
        const totalInvoiceDiscountAmount = (gross * totalInvDiscPercent) / 100;
        const grandTotalBeforeBundle = gross;
        const hasBundlePromoVatLocal = Number.isFinite(bundlePromoVat) && bundlePromoVat > 0.0001;
        const vatExcludingPromo = hasBundlePromoVatLocal
            ? parseFloat((vatAmount - bundlePromoVat).toFixed(2))
            : 0;
        const promoVatAdjust = hasBundlePromoVatLocal ? bundlePromoVat : 0;
        const localGrandFallback = Math.max(0, grandTotalBeforeBundle - bundlePromoDiscount - promoVatAdjust);
        const apiGrandFallback = parseFloat(apiTotals?.grand_total ?? apiTotals?.grandTotal ?? NaN);
        const hasLineItemDiscountFallback = cartItems.some((it) => {
            const d = parseFloat(it.discount ?? it.item_discount ?? it.manualOverrides?.discount ?? 0);
            return Number.isFinite(d) && d > 0.0001;
        });
        const grandTotal = hasLineItemDiscountFallback
            ? localGrandFallback
            : Number.isFinite(apiGrandFallback)
                ? Math.max(0, apiGrandFallback)
                : localGrandFallback;

        setCartTotals({
            boxCount,
            pieceCount,
            subtotal: subtotalAmount,
            vat: vatAmount,
            stdGoods,
            zeroGoods,
            totalBeforeDiscount,
            discountAmount: totalInvoiceDiscountAmount,
            promoDiscPercent: 0,
            promoDisc: bundlePromoDiscount,
            promoVat: bundlePromoVat,
            vatExcludingPromo,
            grandTotal,
            gross
        });
    }, [cartItems, totalInvDiscPercent, apiTotals, effectiveBundleDiscounts, isTelecaller]);

    const handleQuantityChange = useCallback((cartItem, value, changeType = 'absolute') => {
        const itemId = cartItem.id || cartItem.product_id;

        setCartItems((prev) => {
            const sourceItem = prev.find(item => (item.id === itemId || item.product_id === itemId));
            if (!sourceItem) return prev;

            const currentQty = parseQty(sourceItem, sourceItem.qty);
            let delta;

            if (changeType === 'increment' || changeType === 'decrement') {
                delta = value;
            } else {
                const targetQty = parseQty(sourceItem, value);
                if (!Number.isFinite(targetQty)) return prev;
                delta = targetQty - currentQty;
                if (delta === 0) return prev;
            }

            const isOfferProduct = sourceItem.generated === true || sourceItem.generated === "1" || sourceItem.generated === 1;
            const promosId = sourceItem.promosId || sourceItem.promoId || 0;
            const getQuantity = sourceItem.get_quantity ? parseInt(sourceItem.get_quantity) : null;

            if (isOfferProduct && getQuantity !== null && delta > 0) {
                const totalOfferQty = prev.reduce((sum, item) => {
                    const itemIsOffer = item.generated === true || item.generated === "1" || item.generated === 1;
                    const itemPromosId = item.promosId || item.promoId || 0;

                    if (itemIsOffer && itemPromosId === promosId) {
                        if (item.id === itemId || item.product_id === itemId) {
                            return sum + parseQty(item, item.qty) + delta;
                        } else {
                            return sum + parseQty(item, item.qty);
                        }
                    }
                    return sum;
                }, 0);

                if (totalOfferQty > getQuantity) {
                    setSnackbar({
                        open: true,
                        message: `Cannot increase quantity. Total offer products quantity (${totalOfferQty}) exceeds the maximum allowed (${getQuantity}).`,
                        severity: "warning"
                    });
                    return prev;
                }
            }

            return prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    const newQty = parseQty(item, item.qty) + delta;
                    return {
                        ...item,
                        qty: String(newQty),
                        order_qty: newQty
                    };
                }
                return item;
            });
        });
    }, []);

    const handleTypeChange = useCallback((cartItem, newType) => {
        const itemId = cartItem.id || cartItem.product_id;
        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const newPrice = newType === "Box"
                    ? (parseFloat(item.sales_box_price || item.boxPrice || item.price) || 0)
                    : (parseFloat(item.sales_piece_price || item.piecePrice || item.piece_price1) || 0);

                return {
                    ...item,
                    type: newType,
                    order_type: newType,
                    price: newPrice
                };
            }
            return item;
        }));
    }, []);

    const allowEveUnitPriceEdit =
        !isTelecallerRhpOfferPriceLocked &&
        (isPriceEditableFromSettingsLoaded ? isPriceEditableFromSettings : true);

    const handlePriceChange = useCallback((cartItem, value, options = {}) => {
        const { commit = true } = options;
        const itemId = cartItem.id || cartItem.product_id;
        const parsedValue = commit ? parseFloat(value) : value;
        const numForUnitPrice = Number.isFinite(parseFloat(value)) ? parseFloat(value) : null;

        setCartItems((prev) => prev.map((item) => {
            if (item.id === itemId) {
                const update = { ...item, price: parsedValue };
                if (commit && Number.isFinite(parsedValue)) {
                    update.unit_price = parsedValue;
                } else if (numForUnitPrice !== null) {
                    update.unit_price = numForUnitPrice;
                }
                return update;
            }
            return item;
        }));

        if (commit && customerId) {
            const pid = cartItem.product_id || cartItem.id;
            enqueuePromotionCheck(pid, null);
        }
    }, [customerId, enqueuePromotionCheck]);

    const handleDiscountChange = useCallback((cartItem, value, discountType = "amount") => {
        if (isCartLineOfferDisplayRole(cartItem)) return;
        const parsedValue = parseFloat(value);
        const discountValue = Number.isFinite(parsedValue) ? parsedValue : 0;
        const clearComment = discountValue <= 0;
        setCartItems((prev) => prev.map((item) => {
            if (isCartLineOfferDisplayRole(item)) return item;
            const sameId = cartItem.id != null && item.id != null && String(item.id) === String(cartItem.id);
            if (!sameId && !isSameCartPaidOfferLine(item, cartItem)) {
                return item;
            }
            const nextOverrides = {
                ...(item.manualOverrides || {}),
                discount: discountValue,
                discount_type: discountType,
            };
            if (clearComment) {
                nextOverrides.discount_comment = "";
            }
            return {
                ...item,
                discount: discountValue,
                item_discount: String(discountValue),
                discount_type: discountType,
                productWiseDiscountType: discountType,
                promotion_discount: 0,
                ...(clearComment ? { comment: "", discount_comment: "" } : {}),
                manualOverrides: nextOverrides,
            };
        }));
    }, []);

    const handleDiscountCommentChange = useCallback((cartItem, newComment) => {
        if (isCartLineOfferDisplayRole(cartItem)) return;
        const comment = String(newComment || "").trim();
        setCartItems((prev) => prev.map((item) => {
            if (isCartLineOfferDisplayRole(item)) return item;
            const sameId = cartItem.id != null && item.id != null && String(item.id) === String(cartItem.id);
            if (!sameId && !isSameCartPaidOfferLine(item, cartItem)) {
                return item;
            }
            return {
                ...item,
                comment,
                discount_comment: comment,
                manualOverrides: {
                    ...(item.manualOverrides || {}),
                    discount_comment: comment,
                },
            };
        }));
    }, []);

    const handleDeleteItem = useCallback((itemId) => {
        setCartItems((prev) => {
            const updatedItems = prev.filter((item) => item.id !== itemId && item.product_id !== itemId);

            if (customerId && updatedItems.length > 0) {
                enqueuePromotionCheck(itemId, null);
            }

            return updatedItems;
        });
    }, [customerId, enqueuePromotionCheck]);

    const clearCart = useCallback(() => {
        resetStickyBundleDiscountState(stickyBundleStateRef.current);
        setCartItems([]);
    }, []);

    useEffect(() => {
        if (!orderData) return;
        if (!orderData?.success) {
            setSnackbar({ open: true, message: orderData?.message || "Failed to load order", severity: "error" });
            return;
        }
        const details = orderData?.details || {};
        const order = Array.isArray(details?.orderDetails) ? details.orderDetails[0] : null;
        const products = Array.isArray(details?.product_details) ? details.product_details : [];

        setOrderHeader(order || null);
        const orderAccountNo = order?.accound_no ?? order?.account_no ?? "";
        const orderCustomer = order ? {
            order_id: order?.order_id,
            id: order?.customer_id,
            name: order?.name,
            company: order?.company || "",
            address: order?.delivery_address || order?.address || "",
            accound_no: orderAccountNo,
            account_no: orderAccountNo,
        } : null;
        setSelectedCustomer(orderCustomer);
        if (order) {
            setCustomerSearchInput(formatCustomerDisplayLabel(orderCustomer));
        }

        if (order?.customer_id) {
            setCustomerId(order.customer_id);
        }

        if (order?.channel) {
            setOrderChannel(order.channel);
        }

        setOriginalOrderProducts(products);

        const orderDiscount = Number(order?.order_discount ?? 0) || 0;
        setTotalInvDiscPercent(orderDiscount);
        setDeliveryAddress(order?.delivery_address || order?.address || "");
        setStaffNote(order?.staff_note || "");
    }, [orderData]);

    useEffect(() => {
        if (!customerId) return;

        if (!originalOrderProducts?.length) {
            setSnackbar({ open: true, message: "No products found for this order", severity: "info" });
            setCartItems([]);
            return;
        }

        resetStickyBundleDiscountState(stickyBundleStateRef.current);

        const transformedProducts = transformProductDetailsToCart(originalOrderProducts);

        setCartItems([]);

        const cartDataToUse = {
            data: transformedProducts,
            success: true
        };

        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderIdForPayload);
        applyPromotionCheckTrigger(payload, PROMOTION_MESSAGE_TRIGGERS.PAGE_LOAD);
        checkPromotionMutation.mutate(payload);
    }, [customerId, originalOrderProducts, orderHeader, promotionChannel]);

    useEffect(() => {
        if (Object.keys(promotionCheckQueue).length === 0) return;
        if (!customerId) return;

        if (promotionCheckTimerRef.current) {
            clearTimeout(promotionCheckTimerRef.current);
        }

        promotionCheckTimerRef.current = setTimeout(() => {
            const queueSnapshot = { ...promotionCheckQueueRef.current };
            const queueMeta = resolvePromotionCheckQueueMeta(queueSnapshot);
            const triggerMeta = promotionCheckTriggerRef.current || {};
            const trigger =
                queueMeta.trigger ||
                triggerMeta.trigger ||
                null;
            const changedProductId =
                queueMeta.changedProductId ||
                triggerMeta.changedProductId ||
                null;
            const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
            const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
            const cartDataToUse = {
                data: cartItemsRef.current.length > 0
                    ? cartItemsRef.current
                    : transformProductDetailsToCart(originalOrderProducts),
                success: true
            };
            const payload = transformCartToPromotionPayload(
                cartDataToUse,
                customerId,
                orderDate,
                promotionChannel,
                orderTime,
                orderIdForPayload
            );
            applyPromotionCheckTrigger(payload, trigger, changedProductId);
            promotionCheckTriggerRef.current = { trigger: null, changedProductId: null };
            promotionCheckQueueRef.current = {};
            checkPromotionMutation.mutate(payload, {
                onSuccess: () => {
                    setPromotionCheckQueue({});
                },
                onError: () => {
                    setSnackbar({ open: true, message: "Failed to check promotions", severity: "error" });
                    setPromotionCheckQueue({});
                }
            });
        }, 500);

        return () => {
            if (promotionCheckTimerRef.current) {
                clearTimeout(promotionCheckTimerRef.current);
            }
        };
    }, [promotionCheckQueue, orderHeader, customerId, promotionChannel, originalOrderProducts]);

    useEffect(() => {
        if (isError) {
            setSnackbar({ open: true, message: "Failed to load order", severity: "error" });
        }
    }, [isError]);

    useEffect(() => {
        cartItemsRef.current = cartItems;
        cartQtyValidationRef.current = syncCartQtyValidationMap(cartItems, {
            qtyField: "allot",
        });
    }, [cartItems]);

    useEffect(() => {
        if (cartItems.length !== 0) return;
        setPromotionSummary(null);
        setBundleDiscounts([]);
        setApiTotals(null);
    }, [cartItems.length]);

    useEffect(() => {
        return () => {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }
            if (promotionCheckTimerRef.current) {
                clearTimeout(promotionCheckTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        calculateCartTotals();
    }, [cartItems, totalInvDiscPercent, calculateCartTotals]);

    useEffect(() => {
        const currentItems = cartItemsRef.current;
        if (currentItems.length === 0) {
            if (lastDiscountCalculationRef.current.totalDiscountAmount !== 0) {
                lastDiscountCalculationRef.current = { totalDiscountAmount: 0, itemsHash: "" };
            }
            return;
        }

        const itemsHash = currentItems.map(item =>
            `${item.id || item.product_id}_${item.price}_${item.qty}_${item.discount}_${item.discount_type || 'amount'}`
        ).join('|');

        const totalDiscountAmount = (() => {
            let subtotalAmount = 0;
            let vatAmount = 0;

            currentItems.forEach((item) => {
                const price = parseFloat(item.price) || 0;
                const qty = parseInt(item.qty) || 0;
                const discount = parseFloat(item.discount || 0);
                const vatRate = parseFloat(item.rate || item.tax_rate || 0);
                const taxMethod = item.tax_method || "exclusive";
                const isVatInclusive = taxMethod === "inclusive";

                const itemDiscountType = item.discount_type || "amount";
                const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
                const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

                let baseTotal = price * qty;

                let discountedTotal;
                if (isFixedDiscount) {
                    const discountAmount = parseFloat(discount) || 0;
                    discountedTotal = Math.max(0, baseTotal - discountAmount);
                } else {
                    const multiplier = Math.min(Math.max(discount, 0), 100);
                    discountedTotal = baseTotal * (1 - multiplier / 100);
                }

                let itemSubtotal;
                let itemVat;
                if (isVatInclusive && vatRate > 0) {
                    itemSubtotal = discountedTotal / (1 + vatRate / 100);
                    itemVat = discountedTotal - itemSubtotal;
                } else {
                    itemSubtotal = discountedTotal;
                    itemVat = itemSubtotal > 0 ? (itemSubtotal * vatRate) / 100 : 0;
                }

                subtotalAmount += itemSubtotal;
                vatAmount += itemVat;
            });

            const totalBeforeDiscount = subtotalAmount + vatAmount;
            return (totalBeforeDiscount * totalInvDiscPercent) / 100;
        })();

        const needsUpdate =
            Math.abs(lastDiscountCalculationRef.current.totalDiscountAmount - totalDiscountAmount) > 0.01 ||
            lastDiscountCalculationRef.current.itemsHash !== itemsHash;

        if (needsUpdate) {
            if (totalDiscountAmount > 0) {
                const updatedItems = calculateProportionalInvoiceDiscount(currentItems, totalDiscountAmount);
                setCartItems(updatedItems);
            } else {
                const hasNonZeroDiscount = currentItems.some(item => parseFloat(item.invoiceDiscountAmount || 0) > 0);
                if (hasNonZeroDiscount) {
                    setCartItems(currentItems.map(item => ({ ...item, invoiceDiscountAmount: 0 })));
                }
            }
            lastDiscountCalculationRef.current = {
                totalDiscountAmount,
                itemsHash
            };
        }
    }, [cartTotals.totalBeforeDiscount, cartTotals.discountAmount, totalInvDiscPercent, calculateProportionalInvoiceDiscount]);

    const handleCancel = () => {
        setCancelConfirmDialog(true);
    };

    const handleSave = () => {
        if (cartItems.some(cartLineMissingDiscountComment)) {
            showSnackbar("Comment is required when discount is applied", "error");
            return;
        }
        setSaveConfirmDialog(true);
    };

    const buildUpdatePayload = (status = "Accept") => {
        const userInfo = localStorage.getItem("userId");
        const userId = userInfo || "0";
        const userRole = localStorage.getItem("user_role");

        let channelValue = "4";
        if (userRole === "20") {
            channelValue = "5";
        } else if (userRole === "18") {
            channelValue = "4";
        }

        const currentDate = new Date();
        const orderDate = orderHeader?.order_date || formatDate(currentDate);
        const orderTime = orderHeader?.order_time || formatTime(currentDate);

        const currentCartItems = cartItemsRef.current.length > 0 ? cartItemsRef.current : cartItems;

        const validCartItems = currentCartItems.filter(item => {
            const productId = item.product_id || item.productId;
            const qty = parseQty(item, item.allot_qty ?? item.qty ?? item.order_qty);
            return productId && qty > 0;
        });

        const usedOriginalLineIdsForInvoice = new Set();

        const orderList = validCartItems.map((item) => {
            const qty = parseQty(item, item.allot_qty ?? item.qty ?? item.order_qty);
            const price = parseFloat(item.price) || 0;
            const unitPrice = resolveInvoicePayloadUnitPrice(item);
            const effectiveUnitPrice = unitPrice;
            const discount = parseFloat(item.discount || 0);
            const vatRate = parseFloat(item.rate || item.tax_rate || 0);
            const taxMethod = item.tax_method || "exclusive";
            const isVatInclusive = taxMethod === "inclusive";

            const isPromotionGenerated = item.generated === true || item.generated === "1" || item.generated === 1;
            const promotionId = item.promoId || item.promosId || null;
            const promotionType = item.promoType || item.promosType || null;
            const isPromoted = item.is_promoted === "1" || item.is_promoted === 1;

            const promoDiscountPercentage = parseFloat(item.promo_disc_percentage ?? 0) || 0;
            const promoDiscountAmount = parseFloat(item.promo_disc_amount ?? 0) || 0;
            const originalPrice = parseFloat(item.original_price ?? item.unit_price ?? price ?? 0);

            const itemDiscountType =
                item.discount_type ||
                item.productWiseDiscountType ||
                item.item_discount_type ||
                "amount";
            const normalizedDiscountType = String(itemDiscountType).trim().toLowerCase();
            const isFixedDiscount = normalizedDiscountType === "fixed" || normalizedDiscountType === "£" || normalizedDiscountType === "amount";

            let finalSubtotal;
            let finalItemVat;
            let productWiseDiscountPercentage;
            let productWiseDiscountAmount;
            let productWiseDiscountTypePayload;

            const baseTotal = effectiveUnitPrice * qty;
            const apiProductWiseDiscount = resolveProductWiseDiscountFromPromotionApi(item, baseTotal);

            let discountedTotal;
            if (apiProductWiseDiscount !== null) {
                discountedTotal = baseTotal;
                productWiseDiscountTypePayload = apiProductWiseDiscount.productWiseDiscountType;
                productWiseDiscountPercentage = apiProductWiseDiscount.productWiseDiscountPercentage;
                productWiseDiscountAmount = apiProductWiseDiscount.productWiseDiscountAmount;
            } else if (isFixedDiscount) {
                const discountAmount = parseFloat(discount) || 0;
                discountedTotal = Math.max(0, baseTotal - discountAmount);
                productWiseDiscountAmount = discountAmount;
                productWiseDiscountPercentage = baseTotal > 0 ? (discountAmount / baseTotal) * 100 : 0;
                productWiseDiscountTypePayload = "amount";
            } else {
                const multiplier = Math.min(Math.max(discount, 0), 100);
                discountedTotal = baseTotal * (1 - multiplier / 100);
                productWiseDiscountPercentage = discount;
                productWiseDiscountAmount = baseTotal - discountedTotal;
                productWiseDiscountTypePayload = "percentage";
            }

            if (isVatInclusive && vatRate > 0) {
                finalSubtotal = discountedTotal / (1 + vatRate / 100);
                finalItemVat = discountedTotal - finalSubtotal;
            } else {
                finalSubtotal = discountedTotal;
                finalItemVat = finalSubtotal > 0 ? (finalSubtotal * vatRate) / 100 : 0;
            }
            const productId = item.product_id || item.productId || null;
            const productCode = item.code || item.product_code || null;
            const productName = item.name || item.product_name || item.desc || null;
            const size = item.size || null;
            const contractPriceApplied = item.isContractPriceApplied || null;
            const contractPrice = item.ContractPrice || null;

            const orderType = (item.type || item.order_type || "box").toLowerCase();
            const originalProduct = resolveOriginalOrderLineForInvoicePayload(
                originalOrderProducts,
                item,
                productId,
                orderType,
                usedOriginalLineIdsForInvoice
            );
            const originalProductId = originalProduct?.id || item.original_product_id || null;
            const id = originalProductId ? String(originalProductId) : "";
            let originalOrderQty = null;
            if (item.original_order_qty !== undefined && item.original_order_qty !== null && item.original_order_qty !== "") {
                originalOrderQty = String(item.original_order_qty);
            } else if (originalProduct?.order_qty !== undefined && originalProduct.order_qty !== null && originalProduct.order_qty !== "") {
                originalOrderQty = String(originalProduct.order_qty);
            } else if (originalProduct?.quantity !== undefined && originalProduct.quantity !== null && originalProduct.quantity !== "") {
                originalOrderQty = String(originalProduct.quantity);
            } else if (originalProduct?.qty !== undefined && originalProduct.qty !== null && originalProduct.qty !== "") {
                originalOrderQty = String(originalProduct.qty);
            }

            const productPayload = {
                product_id: productId !== null ? String(productId) : null,
                id: id || "",
                product_code: productCode !== null ? String(productCode) : null,
                product_name: productName !== null ? String(productName) : null,
                unit_price: String(Number.isFinite(unitPrice) ? unitPrice.toFixed(2) : originalPrice.toFixed(2)),
                quantity: String(qty),
                unit_quantity: String(qty),
                discount: String(discount.toFixed(2)),
                order_type: (item.type || item.order_type || "box").toLowerCase(),
                order_qty: originalOrderQty !== null ? originalOrderQty : String(qty),
                item_tax: String(finalItemVat.toFixed(2)),
                subtotal: String(finalSubtotal.toFixed(2)),
                tax_rate_percent: String(vatRate),
                is_promoted: isPromoted ? "1" : "0",
                size: size !== null ? String(size) : null,
                picked_qty: "0.00",
                accept_qty: String(item.allot_qty !== undefined ? item.allot_qty : qty),
                proforma_qty: "0",
                item_discount: String(discount.toFixed(2)),
                discount_comment: !isCartLineOfferDisplayRole(item) && discount > 0.0001
                    ? String(item.discount_comment || item.manualOverrides?.discount_comment || item.comment || "").trim()
                    : "",
                comment: !isCartLineOfferDisplayRole(item) && discount > 0.0001
                    ? String(item.comment || item.discount_comment || item.manualOverrides?.discount_comment || "").trim()
                    : "",
                productWiseDiscountType: productWiseDiscountTypePayload,
                productWiseDiscountPercentage: String(productWiseDiscountPercentage.toFixed(2)),
                productWiseDiscountAmount: String(productWiseDiscountAmount.toFixed(2)),
                promoDiscountPercentage: String(promoDiscountPercentage.toFixed(2)),
                promoDiscountAmount: String(promoDiscountAmount.toFixed(2)),
                deliver_qty: "0.00",
                isContractPriceApplied: contractPriceApplied !== null ? String(contractPriceApplied) : "N",
                ContractPrice: contractPrice !== null ? String(contractPrice) : null,
                promo_name: promotionType !== null ? String(promotionType) : null,
                free_product: isPromotionGenerated ? "Y" : "N",
                promo_product: isPromoted ? "Y" : "N",
                promo_id: promotionId !== null ? String(promotionId) : "0",
                promotion_applied: isPromoted ? "1" : "0",
                generated: isPromotionGenerated ? "1" : "0",
            };

            return productPayload;
        });

        return {
            orderId: orderId,
            status: status,
            group_id: String(orderHeader?.group_id || ""),
            delivery_address: deliveryAddress || "",
            over_all_amount: String(cartTotals.discountAmount?.toFixed(2) || "0.00"),
            over_all_discount: String(totalInvDiscPercent || "0.00"),
            orderList: orderList,
            staffNote: staffNote || "",
            order_date: orderDate,
            order_time: orderTime,
            userId: String(userId),
            channel: channelValue,
        };
    };

    const updateOrderStatus = async (status) => {
        // const accessToken = localStorage.getItem('accessToken');
        const accessToken = localStorage.getItem('token');
        const userId = localStorage.getItem('userId');
        const formData = new FormData();
        formData.append('status', status);
        formData.append('id', orderId);
        formData.append('reason_note', '');
        formData.append('user_id', userId || '');
        formData.append('new-sales', 'No');

        const response = await fetch(`${config.API_URL}/ws-update-status`, {
            method: 'POST',
            headers: {
                'authorization': `Bearer ${accessToken}`,
            },
            body: formData
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        return responseData;
    };

    const isRhpPrintTenantLocal = () => isRhpPrintTenant();

    const getDeliveryNotePrintUrl = (id) => {
        const accessToken = localStorage.getItem('accessToken');
        if (isRhpPrintTenantLocal()) {
            return `${config.BASE_URL}/invoice/rhp_delivery_note.html?orderId=${id}`;
        }
        return `${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`;
    };

    const getDrinkSheetPrintUrl = (id) => {
        const accessToken = localStorage.getItem('accessToken');
        if (isRhpPrintTenantLocal()) {
            return `${config.BASE_URL}/invoice/rhp_drink_sheet.html?orderId=${id}`;
        }
        return `${config.BASE_URL}/invoice/sales_invoice.html?orderId=${id}&prefix=${encodeURIComponent(accessToken || '')}`;
    };

    const closePrintWindow = (printWindow) => {
        if (printWindow && !printWindow.closed) {
            printWindow.close();
        }
    };

    const openUrlInNamedTab = (url, targetName) => {
        try {
            const namedWin = window.open(url, targetName);
            if (namedWin && !namedWin.closed) {
                return namedWin;
            }
        } catch (openError) {
        }

        const form = document.createElement('form');
        form.method = 'GET';
        form.target = targetName;
        form.style.display = 'none';
        try {
            const parsed = new URL(url, window.location.origin);
            form.action = `${parsed.origin}${parsed.pathname}`;
            parsed.searchParams.forEach((value, key) => {
                const input = document.createElement('input');
                input.type = 'hidden';
                input.name = key;
                input.value = value;
                form.appendChild(input);
            });
        } catch (parseError) {
            form.action = url;
        }
        document.body.appendChild(form);
        form.submit();
        form.remove();
        return null;
    };

    const navigateNamedPrintTab = (url, targetName) => {
        if (!url || !targetName) {
            return null;
        }

        const opened = openUrlInNamedTab(url, targetName);
        if (opened && !opened.closed) {
            return opened;
        }

        try {
            return window.open('', targetName);
        } catch (namedTabError) {
            return null;
        }
    };

    const assignPrintWindowUrl = (printWindow, url, targetName) => {
        const namedTab = navigateNamedPrintTab(url, targetName);
        if (namedTab && !namedTab.closed) {
            return namedTab;
        }

        if (printWindow && !printWindow.closed) {
            try {
                printWindow.location.replace(url);
                return printWindow;
            } catch (assignError) {
                console.warn('Failed to assign print window URL:', assignError);
            }
        }

        try {
            return window.open(url, targetName || '_blank');
        } catch (openError) {
            console.warn('Failed to open print window URL:', openError);
            return null;
        }
    };

    const focusPrintWindow = (printWindow, targetName) => {
        let tab = printWindow;
        if ((!tab || tab.closed) && targetName) {
            try {
                tab = window.open('', targetName);
            } catch (focusLookupError) {
            }
        }
        if (!tab || tab.closed) {
            return;
        }
        try {
            tab.focus();
        } catch (focusError) {
        }
    };

    const createDispatchPrintWindows = (id) => {
        const printSessionId = `${id}_${Date.now()}`;
        const invoiceName = `rhpInvoice_${printSessionId}`;
        const invoice = window.open('about:blank', invoiceName);

        return {
            sessionId: printSessionId,
            invoiceName,
            drink: null,
            invoice,
        };
    };

    const openDispatchPrintDocuments = (id, printWindows = {}) => {
        if (!id) {
            return null;
        }

        const deliveryNoteUrl = getDeliveryNotePrintUrl(id);

        printWindows.invoice = assignPrintWindowUrl(
            printWindows.invoice,
            deliveryNoteUrl,
            printWindows.invoiceName
        );

        if (!printWindows.invoice || printWindows.invoice.closed) {
            printWindows.invoice = window.open(deliveryNoteUrl, printWindows.invoiceName || '_blank');
        }

        return printWindows.invoice;
    };

    const handlePrintInvoice = () => {
        if (!orderId) {
            return;
        }
        setSaveAndInvoiceConfirmDialog(true);
    };

    const handleConfirmSaveAndInvoice = async () => {
        setSaveAndInvoiceConfirmDialog(false);
        if (!orderId) {
            return;
        }

        if (cartItems.some(cartLineMissingDiscountComment)) {
            showSnackbar("Comment is required when discount is applied", "error");
            return;
        }

        const payload = buildUpdatePayload("Delivered");

        if (!payload.orderList || payload.orderList.length === 0) {
            showSnackbar("No products in the order. Please add at least one product.", 'error');
            return;
        }

        const printWindows = createDispatchPrintWindows(orderId);

        try {
            const invoiceData = await updateInvoice(payload);

            if (!invoiceData?.success) {
                closePrintWindow(printWindows.drink);
                closePrintWindow(printWindows.invoice);
                showSnackbar(invoiceData?.message || 'Failed to update invoice', 'error');
                return;
            }

            try {
                await updateOrderStatus("Delivered");
            } catch (statusError) {
                console.error("Update Status Error:", statusError);
                showSnackbar("Invoice updated but failed to update status. Please update status manually.", 'warning');
            }

            try {
                await getProformaInvoice(orderId);
            } catch (proformaError) {
                console.warn("Proforma Invoice refresh failed (non-critical):", proformaError);
            }

            const invoiceTab = openDispatchPrintDocuments(orderId, printWindows);
            navigate('/sales/epos_sales');
            if (!invoiceTab || invoiceTab.closed) {
                showSnackbar('Invoice tab could not be opened. Please allow pop-ups for this site.', 'warning');
            }
            window.setTimeout(() => {
                focusPrintWindow(printWindows.invoice, printWindows.invoiceName);
            }, 400);
        } catch (error) {
            closePrintWindow(printWindows.drink);
            closePrintWindow(printWindows.invoice);
            console.error("Update Invoice Error:", error);
            showSnackbar(error.message || 'Failed to update invoice', 'error');
        }
    };

    const handleCloseSaveAndInvoiceConfirmDialog = () => {
        setSaveAndInvoiceConfirmDialog(false);
    };

    const handlePrintDeliveryNote = (id) => {
        if (id) {
            window.open(getDeliveryNotePrintUrl(id), '_blank', 'noopener,noreferrer');
        }
    };

    const handlePrintDrinkSheet = (id) => {
        if (id) {
            window.open(getDrinkSheetPrintUrl(id), '_blank', 'noopener,noreferrer');
        }
    };

    const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

    const showSnackbar = (message, severity = "error") => {
        setSnackbar({ open: true, message, severity });
    };

    const focusProductSearchInput = useCallback((immediate = false) => {
        const run = () => {
            if (!selectedCustomer?.id) return;
            if (isTelecaller) {
                telecallerProductSearchRef.current?.focus?.();
            } else {
                productSearchRef.current?.focusInput?.();
            }
        };
        if (immediate) {
            run();
            return;
        }
        requestAnimationFrame(() => {
            setTimeout(run, 50);
        });
    }, [selectedCustomer?.id, isTelecaller]);

    const refocusProductSearchUnlessCartQtyFocused = useCallback(
        (immediate = false) => {
            if (typeof document !== "undefined") {
                const active = document.activeElement;
                if (active instanceof Element) {
                    if (
                        active.closest("[data-cart-qty-scope]") ||
                        active.closest("[data-cart-row]") ||
                        (retainsCartDiscountFocus && active.closest("[data-cart-discount-input]"))
                    ) {
                        return;
                    }
                }
            }
            focusProductSearchInput(immediate);
        },
        [focusProductSearchInput]
    );

    const handleValidatedTypeChange = useCallback((cartItem, newType) => {
        handleTypeChange(cartItem, newType);
        refocusProductSearchUnlessCartQtyFocused();
    }, [handleTypeChange, refocusProductSearchUnlessCartQtyFocused]);

    useEffect(() => {
        if (!orderId) return;
        if (isLoading) return;
        if (!orderData || orderData.success !== true) return;
        if (!selectedCustomer?.id) return;

        const timer = window.setTimeout(() => {
            focusProductSearchInput(true);
        }, 150);
        return () => clearTimeout(timer);
    }, [orderId, isLoading, orderData?.success, selectedCustomer?.id, focusProductSearchInput]);

    const validateQuantity = (value, cartItem = null) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        const allowDecimal = cartItem ? allowsDecimalQty(cartItem) : false;
        const numValue = allowDecimal ? parseFloat(value) : Number(value);
        if (isNaN(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid quantity");
            return false;
        }
        if (allowDecimal) {
            if (numValue < 0.01 || numValue > 9999.99) {
                showSnackbar("Quantity must be between 0.01 and 9999.99");
                return false;
            }
            const decimalPart = String(value).split(".")[1];
            if (decimalPart && decimalPart.length > 2) {
                showSnackbar("Quantity can have at most 2 decimal places");
                return false;
            }
        } else if (!Number.isInteger(numValue) || numValue < 1 || numValue > 9999) {
            showSnackbar("Quantity must be between 1 and 9999");
            return false;
        }
        return true;
    };

    const handleDeleteItemClick = (itemId) => {
        setDeleteItemDialog({ open: true, itemId });
    };

    const handleConfirmDeleteItem = () => {
        if (deleteItemDialog.itemId) {
            handleDeleteItem(deleteItemDialog.itemId);
        }
        setDeleteItemDialog({ open: false, itemId: null });
        focusProductSearchInput();
    };

    const handleCloseDeleteItemDialog = () => {
        setDeleteItemDialog({ open: false, itemId: null });
        focusProductSearchInput();
    };

    const revertCartAllotQty = useCallback((cartItem) => {
        if (!cartItem) return;
        const lineKey = getCartLineValidationKey(cartItem);
        const revertQty = lastValidAllotQtyRef.current.get(lineKey);
        if (revertQty === undefined) return;
        const itemId = cartItem.id || cartItem.product_id;
        const allotStr = isMeatsProduct(cartItem)
            ? String(revertQty)
            : String(Math.round(revertQty));
        cartQtyValidationRef.current.set(lineKey, revertQty);
        setCartItems((prev) => {
            const next = prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    return { ...item, allot_qty: allotStr, qty: allotStr };
                }
                return item;
            });
            cartItemsRef.current = next;
            return next;
        });
    }, []);

    const handleCloseTelecallerStockDialog = useCallback(() => {
        const pending = stockLimitRevertRef.current;
        if (pending?.cartItem) {
            revertCartAllotQty(pending.cartItem);
        }
        stockLimitRevertRef.current = null;
        setTelecallerStockDialog(EMPTY_SALES_LIMIT_DIALOG);
        focusProductSearchInput();
    }, [focusProductSearchInput, revertCartAllotQty]);

    const showPurchaseLimitPopup = useCallback((message, sourceItem) => {
        stockLimitRevertRef.current = { cartItem: sourceItem };
        setTelecallerStockDialog(buildPurchaseLimitDialogState(sourceItem, message));
    }, []);

    const showStockLimitPopup = useCallback((sourceItem, cap) => {
        stockLimitRevertRef.current = { cartItem: sourceItem };
        setTelecallerStockDialog(buildStockLimitDialogState(sourceItem, cap));
    }, []);

    const formatDate = (date) => {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatTime = (date) => {
        const d = new Date(date);
        const hours = String(d.getHours()).padStart(2, '0');
        const minutes = String(d.getMinutes()).padStart(2, '0');
        const seconds = String(d.getSeconds()).padStart(2, '0');
        return `${hours}:${minutes}:${seconds}`;
    };

    const extractDateAndTime = (dateString) => {
        if (!dateString) {
            const now = new Date();
            return {
                date: formatDateForAPI(now),
                time: formatTime(now)
            };
        }

        if (typeof dateString === 'string' && dateString.includes(' ')) {
            const parts = dateString.split(' ');
            const datePart = parts[0];
            const timePart = parts[1] || '00:00:00';
            return {
                date: datePart,
                time: timePart
            };
        }

        const d = new Date(dateString);
        return {
            date: formatDateForAPI(d),
            time: formatTime(d)
        };
    };

    const handleConfirmSave = () => {
        if (cartItems.some(cartLineMissingDiscountComment)) {
            showSnackbar("Comment is required when discount is applied", "error");
            setSaveConfirmDialog(false);
            return;
        }
        const payload = buildUpdatePayload("Accept");
        updateInvoiceMutation.mutate(payload);
    };

    const handleCloseSaveConfirmDialog = () => {
        setSaveConfirmDialog(false);
    };

    const handleConfirmCancel = () => {
        setCancelConfirmDialog(false);
        clearCart();
        setTotalInvDiscPercent(0);
        releaseLock(false);
        navigate(-1);
    };

    const handleCloseCancelConfirmDialog = () => {
        setCancelConfirmDialog(false);
    };

    const validatePrice = (value, item) => {
        if (!value || value === "") {
            showSnackbar("Please enter a valid price");
            return false;
        }
        if (item?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Please enter a valid price (e.g., 45.99)");
            return false;
        }
        if (numValue < 0.01 || numValue > 9999.99) {
            showSnackbar("Price must be between £0.01 and £9,999.99");
            return false;
        }
        return true;
    };

    const validateDiscount = (value, discountType = "percentage", cartItem = null) => {
        if (value === "" || value === null || value === undefined) {
            return true;
        }

        const numValue = Number(value);

        if (isNaN(numValue)) {
            showSnackbar(discountType === "percentage" ? "Please enter a valid discount percentage" : "Please enter a valid discount amount");
            return false;
        }

        if (numValue < 0) {
            showSnackbar("Discount cannot be negative");
            return false;
        }

        if (discountType === "percentage") {
            if (numValue > 100) {
                showSnackbar("Discount cannot exceed 100%");
                return false;
            }
        } else {
            const isFixed = discountType === "amount" || discountType === "£" || discountType === "fixed";
            if (isFixed && cartItem) {
                const orderType = cartItem.type || cartItem.order_type || "Box";
                const qty = (cartItem.order_qty !== undefined && cartItem.order_qty !== null && cartItem.order_qty !== "")
                    ? parseQty(cartItem, cartItem.order_qty)
                    : parseQty(cartItem, cartItem.qty);
                let unitPrice = 0;
                if (cartItem.unit_price !== undefined && cartItem.unit_price !== null && cartItem.unit_price !== "") {
                    unitPrice = parseFloat(cartItem.unit_price);
                } else if (orderType === "Box") {
                    unitPrice = parseFloat(cartItem.sales_box_price || 0);
                } else {
                    unitPrice = parseFloat(cartItem.sales_piece_price || 0);
                }
                if (unitPrice > 0 && numValue > unitPrice) {
                    showSnackbar(`Discount amount cannot be greater than unit price (£${unitPrice.toFixed(2)})`);
                    return false;
                }
            }
        }

        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        if (decimalPlaces > 2) {
            showSnackbar("Discount can have maximum 2 decimal places");
            return false;
        }

        return true;
    };

    const checkPriceEditPermission = () => {
        const hasPermission = true;
        if (!hasPermission) {
            showSnackbar("You don't have permission to edit prices. Contact your manager.");
            return false;
        }
        return true;
    };

    const handleValidatedQuantityChange = (cartItem, value, changeType = 'absolute') => {
        const sourceItem = cartItem?.originalItem || cartItem;

        if (changeType !== 'increment' && changeType !== 'decrement' && !validateQuantity(value, cartItem)) {
            return false;
        }

        const result = computeCartQuantityChange({
            sourceItem,
            validationMap: cartQtyValidationRef.current,
            value,
            changeType,
            qtyField: "qty",
            shouldCheckStockBeforeAddToCart,
            shouldSkipStockCheck: (item) => shouldSkipTelecallerStockLimit(item),
            cartItems: cartItemsRef.current,
        });

        const accepted = applyCartQtyValidationResult(result, {
            onRange: (r) => {
                showSnackbar(`Quantity must be between ${r.minQty} and ${r.maxQty}`, "warning");
            },
            onPurchaseLimit: (r) => {
                showPurchaseLimitPopup(r.message, sourceItem);
            },
            onStock: (r) => {
                showStockLimitPopup(sourceItem, r.cap);
            },
        });

        if (!accepted) {
            return false;
        }

        cartQtyValidationRef.current.set(result.lineKey, result.finalQty);
        handleQuantityChange(cartItem, value, changeType);

        if (customerId) {
            setTimeout(() => {
                const pid = cartItem?.product_id || cartItem?.productId || cartItem?.id;
                enqueuePromotionCheck(pid, PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE);
            }, 0);
        }

        refocusProductSearchUnlessCartQtyFocused();
        return true;
    };

    const handleValidatedPriceChange = (cartItem, value, options = {}) => {
        if (isTelecallerRhpOfferPriceLocked || !allowEveUnitPriceEdit) {
            return false;
        }
        if (!checkPriceEditPermission()) {
            return;
        }
        const itemDetails = cartItem;
        if (itemDetails?.is_promoted === "1" && itemDetails?.type === "FREE") {
            showSnackbar("Cannot edit price for free promotional items");
            return false;
        }

        if (options?.commit === false) {
            handlePriceChange(cartItem, value, options);
            return true;
        }

        if (validatePrice(value, cartItem)) {
            handlePriceChange(cartItem, value, options);
            focusProductSearchInput();
            return true;
        }

        return false;
    };

    const handleValidatedDiscountChange = (cartItem, value, discountType = "amount") => {
        if (validateDiscount(value, discountType, cartItem)) {
            handleDiscountChange(cartItem, value, discountType);
            if (!retainsCartDiscountFocus) {
                focusProductSearchInput();
            }
        }
    };

    const handleDiscountChangeComplete = useCallback((cartItem, value, discountType) => {
        if (customerId) {
            setTimeout(() => {
                const pid = cartItem?.product_id || cartItem?.productId || cartItem?.id;
                enqueuePromotionCheck(pid, null);
            }, 0);
        }
        if (retainsCartDiscountFocus) {
            focusProductSearchInput();
        }
    }, [customerId, focusProductSearchInput, enqueuePromotionCheck]);

    const handleValidatedTotalInvDiscChange = (value) => {
        if (validateDiscount(value, "percentage", null)) {
            setTotalInvDiscPercent(value);
            focusProductSearchInput();
        }
    };

    const handleAllotQtyChange = useCallback((cartItem, value) => {
        if (value === "" || value === "." || value === null || value === undefined) {
            return false;
        }

        const allowDecimal = isMeatsProduct(cartItem);
        if (!allowDecimal && String(value).includes(".")) {
            showSnackbar("This category won't support this type of quantity", "error");
            return false;
        }

        const numValue = allowDecimal ? parseFloat(value) : parseInt(value, 10);
        if (isNaN(numValue) || numValue < 0) {
            showSnackbar("Please enter a valid allot quantity", "error");
            return false;
        }

        const itemId = cartItem.id || cartItem.product_id;

        if (numValue === 0) {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }
            setCartItems((prev) => {
                const next = prev.filter((item) => !(item.id === itemId || item.product_id === itemId));
                cartItemsRef.current = next;
                if (customerId && next.length > 0) {
                    enqueuePromotionCheck(
                        cartItem?.product_id || cartItem?.id || itemId,
                        PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE
                    );
                }
                return next;
            });
            cartQtyValidationRef.current.delete(getCartLineValidationKey(cartItem));
            return true;
        }

        const sourceItem = cartItem?.originalItem || cartItem;
        const result = computeCartQuantityChange({
            sourceItem,
            validationMap: cartQtyValidationRef.current,
            value: numValue,
            changeType: "absolute",
            qtyField: "allot",
            shouldCheckStockBeforeAddToCart,
            shouldSkipStockCheck: (item) => shouldSkipTelecallerStockLimit(item),
            cartItems: cartItemsRef.current,
        });

        const accepted = applyCartQtyValidationResult(result, {
            onRange: (r) => {
                showSnackbar(`Allot Qty must be between ${r.minQty} and ${r.maxQty}`, "error");
            },
            onPurchaseLimit: (r) => {
                showPurchaseLimitPopup(r.message, sourceItem);
            },
            onStock: (r) => {
                showStockLimitPopup(sourceItem, r.cap);
            },
        });

        if (!accepted) {
            revertCartAllotQty(sourceItem);
            return false;
        }

        if (!shouldCheckStockBeforeAddToCart && isTelecaller && !shouldSkipTelecallerStockLimit(cartItem)) {
            const maxAvail = getTelecallerMaxAvailableQty(cartItem);
            if (maxAvail !== null && result.finalQty > maxAvail + 1e-9) {
                showStockLimitPopup(cartItem, maxAvail);
                revertCartAllotQty(sourceItem);
                return false;
            }
        }

        cartQtyValidationRef.current.set(result.lineKey, result.finalQty);
        lastValidAllotQtyRef.current.set(result.lineKey, result.finalQty);

        setCartItems((prev) => {
            const next = prev.map((item) => {
                if (item.id === itemId || item.product_id === itemId) {
                    return {
                        ...item,
                        allot_qty: value,
                        qty: value,
                        order_qty: value,
                    };
                }
                return item;
            });
            cartItemsRef.current = next;
            return next;
        });

        if (customerId) {
            if (allotQtyDebounceTimerRef.current) {
                clearTimeout(allotQtyDebounceTimerRef.current);
            }

            allotQtyDebounceTimerRef.current = setTimeout(() => {
                enqueuePromotionCheck(
                    cartItem?.product_id || cartItem?.productId || cartItem?.id,
                    PROMOTION_MESSAGE_TRIGGERS.QTY_CHANGE
                );
            }, 500);
        }
        return true;
    }, [customerId, showSnackbar, showPurchaseLimitPopup, showStockLimitPopup, isTelecaller, shouldCheckStockBeforeAddToCart, revertCartAllotQty, enqueuePromotionCheck]);

    const handleCustomerChange = (newCustomer) => {
        setSelectedCustomer(newCustomer);
        if (newCustomer?.id) {
            setCustomerId(newCustomer.id);
        }
    };

    const handleOpenInventoryModal = () => {
        if (!selectedCustomer?.id) {
            showSnackbar("Please select a customer first", "warning");
            return;
        }
        const inputRect = telecallerProductSearchRef.current?.getBoundingClientRect?.();
        if (inputRect) {
            setInventoryModalAnchorPosition({
                top: inputRect.bottom + 8,
                left: inputRect.left,
                width: inputRect.width,
            });
        }
        setInventoryModalOpen(true);
    };

    const handleCloseInventoryModal = () => {
        setInventoryModalOpen(false);

        setProductSearchInput("");
        focusProductSearchInput(true);
    };

    const handleTelecallerProductSearchChange = (event) => {
        const value = event.target.value;
        setProductSearchInput(value);
        if (!selectedCustomer?.id) {
            return;
        }
        if (hasSearchableProductQuery(value) && !inventoryModalOpen) {
            const inputRect = telecallerProductSearchRef.current?.getBoundingClientRect?.();
            if (inputRect) {
                setInventoryModalAnchorPosition({
                    top: inputRect.bottom + 8,
                    left: inputRect.left,
                    width: inputRect.width,
                });
            }
            setInventoryModalOpen(true);
        }
    };

    const handleTelecallerProductSearchKeyDown = (event) => {
        if (event.key !== "Enter") {
            return;
        }
        event.preventDefault();
        if (!selectedCustomer?.id) {
            showSnackbar("Please select a customer first", "warning");
            return;
        }
        if (hasSearchableProductQuery(productSearchInput)) {
            setInventoryModalOpen(true);
        }
    };

    useEffect(() => {
        if (inventoryModalOpen && isTelecaller) {
            const refocusTimer = setTimeout(() => {
                focusProductSearchInput(true);
            }, 0);
            return () => clearTimeout(refocusTimer);
        }
        return undefined;
    }, [inventoryModalOpen, isTelecaller, focusProductSearchInput]);

    const handleInventoryProductSelect = useCallback((product, options = {}) => {
        if (!product || !selectedCustomer?.id) return;

        const normalizeOrderType = (val) => {
            const raw = String(val ?? "Box").trim().toLowerCase();
            if (raw === "piece" || raw === "p" || raw === "1") return "Piece";
            if (raw === "box" || raw === "b" || raw === "0") return "Box";
            return raw.startsWith("p") ? "Piece" : "Box";
        };

        const skipQuantityPopup = options.skipQuantityPopup === true;
        setInventoryModalOpen(false);

        const productId = String(product.id || product.product_id || "");
        const orderType = normalizeOrderType(resolveOrderTypeFromProduct(product));
        let quantity = parseQty(product, product.quantity ?? product.qty);
        if (isMeatsProduct(product)) {
            if (!Number.isFinite(quantity) || quantity < 0.01) quantity = 0.01;
            if (quantity > 9999.99) quantity = 9999.99;
            quantity = Math.round(quantity * 100) / 100;
        } else {
            if (!Number.isFinite(quantity) || quantity < 1) quantity = 1;
            if (quantity > 9999) quantity = 9999;
            quantity = Math.floor(quantity);
        }

        const availableQtyForType = orderType === "Piece"
            ? (parseFloat(product.availablePieceQty ?? 0) || 0)
            : (parseFloat(product.availableBoxQty ?? 0) || 0);
        if (shouldCheckStockBeforeAddToCart && availableQtyForType <= 0) {
            setTimeout(() => {
                showStockLimitPopup(product, 0);
            }, 0);
            return;
        }

        const currentCart = cartItemsRef.current;
        const existingItem = findMergeableCartLineForProductAdd(
            currentCart,
            productId,
            orderType
        );

        if (existingItem) {
            const meatQtyLine = isMeatsProduct(product) || isMeatsProduct(existingItem);
            const maxQtyLine = meatQtyLine ? 9999.99 : 9999;
            const currentQty = parseQty(existingItem, existingItem.qty);
            const newQty = currentQty + quantity;
            if (newQty > maxQtyLine) {
                showSnackbar(
                    `Quantity cannot exceed ${maxQtyLine}. Please reduce quantity before adding more.`,
                    "warning"
                );
                return;
            }

            const purchaseLimitMerge = validateMaximumOrderQuantityForLineQty(
                existingItem || product,
                currentCart,
                newQty,
                { qtyField: "allot" }
            );
            if (!purchaseLimitMerge.allowed) {
                showPurchaseLimitPopup(purchaseLimitMerge.message, existingItem || product);
                return;
            }

            if (shouldCheckStockBeforeAddToCart) {
                const lineForStock = existingItem?.originalItem || existingItem;
                const lineKey = getCartLineValidationKey(existingItem);
                const stockQtyToCheck = getProductQtyTotalInCart(
                    currentCart,
                    existingItem,
                    "allot",
                    lineKey,
                    newQty
                );
                if (!isQuantityWithinLineStock(lineForStock, stockQtyToCheck, meatQtyLine)) {
                    const cap = getStockCapForCartLineItem(lineForStock);
                    showStockLimitPopup(lineForStock, cap != null ? Math.floor(cap) : 0);
                    return;
                }
            }

            setCartItems((prev) => {
                const next = prev.map((item) => {
                    if (
                        String(item.product_id) === productId &&
                        normalizeOrderType(item.order_type || item.type) === orderType &&
                        isCartLineMergeableOnProductAdd(item)
                    ) {
                        const currentAllotQty = parseQty(item, item.allot_qty ?? item.accept_qty ?? item.qty);
                        const newAllotQty = currentAllotQty + quantity;
                        const qStr = meatQtyLine
                            ? String(Math.round(newQty * 100) / 100)
                            : String(Math.round(newQty));
                        const aStr = meatQtyLine
                            ? String(Math.round(newAllotQty * 100) / 100)
                            : String(Math.round(newAllotQty));
                        return {
                            ...item,
                            qty: qStr,
                            allot_qty: aStr
                        };
                    }
                    return item;
                });
                cartItemsRef.current = next;
                return next;
            });

            if (!skipQuantityPopup) {
                setTimeout(() => setNewlyAddedProductId(productId), 200);
            }

            if (customerId) {
                enqueuePromotionCheck(productId, PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT);
            }
        } else {
            const purchaseLimitNewLine = validateMaximumOrderQuantityForCartAdd(
                {
                    ...product,
                    id: productId,
                    product_id: productId,
                    productId: productId,
                    order_type: orderType,
                    is_promoted: "0",
                    promo_type: "",
                    generated: false,
                    free_product: "N",
                    promotion_applied: false,
                },
                currentCart,
                quantity,
                { qtyField: "allot" }
            );
            if (!purchaseLimitNewLine.allowed) {
                showPurchaseLimitPopup(purchaseLimitNewLine.message, product);
                return;
            }

            const preparedProduct = prepareProductForCart(product);
            const idGen = `np_${Date.now()}`;
            const boxPrice = Number(preparedProduct.sales_box_price ?? preparedProduct.boxPrice ?? preparedProduct.price ?? 0) || 0;
            const piecePrice = Number(preparedProduct.sales_piece_price ?? preparedProduct.piecePrice ?? preparedProduct.piece_price1 ?? preparedProduct.split_price ?? 0) || 0;
            const finalBoxPrice = Number(preparedProduct.final_price) || boxPrice;
            const finalPiecePrice = Number(preparedProduct.final_split_price) || piecePrice;
            const split = Number(preparedProduct.split || preparedProduct.noOfPieceInBox) || 1;
            const siblingSameSku = currentCart.find(
                (line) => String(line.product_id ?? line.productId ?? "") === productId
            );
            const vatRate =
                Number(preparedProduct.rate || preparedProduct.tax_rate) ||
                Number(siblingSameSku?.rate || siblingSameSku?.tax_rate) ||
                0;
            const taxCode =
                resolveProductTaxCode(preparedProduct) ||
                resolveProductTaxCode(siblingSameSku) ||
                resolveProductTaxCode(product) ||
                "";

            const unitPriceForPromotion = orderType === "Box" ? finalBoxPrice : finalPiecePrice;
            const priceForCart = orderType === "Box" ? finalBoxPrice : finalPiecePrice;
            const standardBox = Number(preparedProduct.standard_box_price ?? preparedProduct.price ?? 0) || boxPrice;
            const standardPiece = Number(preparedProduct.standard_piece_price ?? preparedProduct.split_price ?? 0) || piecePrice;
            const originalPriceForItem = (preparedProduct.original_price != null && preparedProduct.original_price !== "")
                ? parseFloat(preparedProduct.original_price)
                : (orderType === "Box" ? standardBox : standardPiece);
            const qtyForLine = isMeatsProduct(product) ? Math.round(quantity * 100) / 100 : quantity;
            const lineNet = Number((priceForCart * qtyForLine).toFixed(2));
            const lineVat = vatRate > 0 && lineNet > 0 ? Number(((lineNet * vatRate) / 100).toFixed(2)) : 0;

            const newItem = {
                id: idGen,
                product_id: preparedProduct.product_id || product.id || product.product_id,
                productId: preparedProduct.product_id || product.id || product.product_id,
                code: preparedProduct.code || preparedProduct.product_code,
                product_code: preparedProduct.code || preparedProduct.product_code,
                name: preparedProduct.name || preparedProduct.product_name,
                product_name: preparedProduct.name || preparedProduct.product_name,
                desc: preparedProduct.name || preparedProduct.product_name,
                type: orderType,
                order_type: orderType,
                split,
                sales_box_price: boxPrice,
                sales_piece_price: piecePrice,
                boxPrice,
                piecePrice,
                qty: isMeatsProduct(product) ? String(Math.round(quantity * 100) / 100) : String(quantity),
                price: priceForCart,
                unit_price: unitPriceForPromotion,
                original_price: Number.isFinite(originalPriceForItem) ? originalPriceForItem : (orderType === "Box" ? standardBox : standardPiece),
                discount: "0",
                discount_type: "amount",
                rate: String(vatRate),
                tax_rate: String(vatRate),
                tax_code: taxCode,
                is_promoted: "0",
                promo_type: "",
                buyOrGet: "",
                promotion_applied: false,
                promotion_id: 0,
                noOfPieceInBox: split,
                price_1: Number(preparedProduct.price_1 ?? 0) || 0,
                price_2: Number(preparedProduct.price_2 ?? 0) || 0,
                price_3: Number(preparedProduct.price_3 ?? 0) || 0,
                price_4: Number(preparedProduct.price_4 ?? 0) || 0,
                price_5: Number(preparedProduct.price_5 ?? 0) || 0,
                piece_price1: Number(preparedProduct.piece_price1 ?? 0) || 0,
                piece_price2: Number(preparedProduct.piece_price2 ?? 0) || 0,
                piece_price3: Number(preparedProduct.piece_price3 ?? 0) || 0,
                piece_price4: Number(preparedProduct.piece_price4 ?? 0) || 0,
                piece_price5: Number(preparedProduct.piece_price5 ?? 0) || 0,
                cost: Number(preparedProduct.cost ?? 0) || 0,
                itemTax: lineVat,
                vat: lineVat,
                tax: lineVat,
                subtotal: lineNet,
                sub_total: lineNet,
                line_subtotal: lineNet,
                availableBoxQty: Number(preparedProduct.availableBoxQty ?? 0) || 0,
                availablePieceQty: Number(preparedProduct.availablePieceQty ?? 0) || 0,
                size: preparedProduct.size || "",
                inner_ean_number: preparedProduct.inner_ean_number || "",
                outer_ean_number: preparedProduct.outer_ean_number || "",
                innerEanNumber: preparedProduct.inner_ean_number || "",
                outerEanNumber: preparedProduct.outer_ean_number || "",
                discountedBoxPrice: 0,
                discountedSplitPrice: 0,
                productDiscountApplicable: Number(preparedProduct.productDiscountApplicable ?? 0) || 0,
                promoId: 0,
                promosId: 0,
                promoType: null,
                ...((preparedProduct.category_name || preparedProduct.categorie_name)
                    ? {
                        category_name: String(preparedProduct.category_name || preparedProduct.categorie_name || "").trim(),
                        categorie_name: String(preparedProduct.categorie_name || preparedProduct.category_name || "").trim()
                    }
                    : {}),
                promosType: null,
                generated: false,
                free_product: "N",
                piece_cost: Number(preparedProduct.piece_cost ?? 0) || 0,
                pieceCost: Number(preparedProduct.piece_cost ?? 0) || 0,
                accept_qty: isMeatsProduct(product) ? String(Math.round(quantity * 100) / 100) : String(quantity),
                allot_qty: isMeatsProduct(product) ? String(Math.round(quantity * 100) / 100) : String(quantity),
                ...(() => {
                    const m = parseFloat(preparedProduct.maximum_order_quantity ?? preparedProduct.maximumOrderQuantity ?? "");
                    return Number.isFinite(m) && m > 0
                        ? { maximum_order_quantity: m, maximumOrderQuantity: m }
                        : {};
                })(),
            };

            if (shouldCheckStockBeforeAddToCart) {
                const meatQtyLine = isMeatsProduct(product);
                const lineKey = getCartLineValidationKey(newItem);
                const stockQtyToCheck = getProductQtyTotalInCart(
                    currentCart,
                    newItem,
                    "allot",
                    lineKey,
                    quantity
                );
                if (!isQuantityWithinLineStock(newItem, stockQtyToCheck, meatQtyLine)) {
                    const cap = getStockCapForCartLineItem(newItem);
                    showStockLimitPopup(newItem, cap != null ? Math.floor(cap) : 0);
                    return;
                }
            }

            const productIdToAdd = String(newItem.product_id || preparedProduct.product_id || product.id || product.product_id);
            setCartItems((prev) => {
                // Tele sales: new products go to the bottom of the order; EPOS keeps them on top.
                const next = isTelecaller ? [...prev, newItem] : [newItem, ...prev];
                cartItemsRef.current = next;
                return next;
            });

            if (!skipQuantityPopup) {
                setTimeout(() => setNewlyAddedProductId(productIdToAdd), 200);
            }

            if (customerId) {
                enqueuePromotionCheck(productIdToAdd, PROMOTION_MESSAGE_TRIGGERS.ADD_PRODUCT);
            }
        }

        setProductSearchInput("");
        showSnackbar("Product added successfully", "success");
        setTimeout(() => {
            focusProductSearchInput();
        }, 0);
    }, [selectedCustomer?.id, customerId, showSnackbar, showPurchaseLimitPopup, showStockLimitPopup, shouldCheckStockBeforeAddToCart, focusProductSearchInput, enqueuePromotionCheck, isTelecaller]);

    const handleInventoryProductSelectForQuantity = useCallback((product) => {
        if (!product || !selectedCustomer?.id) return;
        setInventoryModalOpen(false);
        setSelectedProduct(product);
        setProductSearchInput("");
    }, [selectedCustomer?.id]);

    const handleBarcodeDetected = useCallback(
        async (barcodeValue) => {
            const trimmed = `${barcodeValue}`.trim();
            if (!trimmed || isBarcodeProcessingRef.current || isQuantityPopupOpenRef.current) return;
            if (!selectedCustomer?.id) {
                showSnackbar("Please ensure customer is loaded before scanning", "warning");
                return;
            }
            isBarcodeProcessingRef.current = true;
            try {
                const response = await getProductsList({
                    pattern: trimmed,
                    customer_id: selectedCustomer.id,
                    orderChannel: orderChannel
                });
                const products = response?.product_details || [];
                const firstProduct = products[0];
                if (!firstProduct) {
                    showSnackbar("No product found for scanned code", "warning");
                    return;
                }
                const productWithOrderType = {
                    ...firstProduct,
                    quantity: 1
                };
                handleInventoryProductSelect(productWithOrderType, { skipQuantityPopup: true });
            } catch {
                showSnackbar("Unable to fetch product for scanned code", "error");
            } finally {
                isBarcodeProcessingRef.current = false;
            }
        },
        [selectedCustomer?.id, orderChannel, handleInventoryProductSelect, showSnackbar]
    );

    useEffect(() => {
        if (selectedProduct && selectedCustomer?.id) {
            const productId = String(selectedProduct.id || selectedProduct.product_id || "");
            const normalizeOrderType = (val) => {
                const raw = String(val ?? "Box").trim().toLowerCase();
                if (raw === "piece" || raw === "p" || raw === "1") return "Piece";
                if (raw === "box" || raw === "b" || raw === "0") return "Box";
                return raw.startsWith("p") ? "Piece" : "Box";
            };
            const orderType = normalizeOrderType(resolveOrderTypeFromProduct(selectedProduct));

            const existingItem = findMergeableCartLineForProductAdd(
                cartItems,
                productId,
                orderType
            );

            if (existingItem) {
                const meatPre = isMeatsProduct(existingItem) || isMeatsProduct(selectedProduct);
                const maxPre = meatPre ? 9999.99 : 9999;
                const currentQty = parseQty(existingItem, existingItem.qty);
                if (currentQty >= maxPre) {
                    showSnackbar(`Quantity cannot exceed ${maxPre}. Please reduce quantity before adding more.`, "warning");
                    setSelectedProduct(null);
                    setProductSearchInput("");
                    return;
                }
            }

            setKeypadError("");
            setKeypadInitialOrderType(orderType);
            setSelectedProductForQuantity({
                ...selectedProduct,
                order_type: orderType,
                type: orderType
            });
            setQuantityKeypadOpen(true);
            setSelectedProduct(null);
            setProductSearchInput("");
        }
    }, [selectedProduct, selectedCustomer, handleInventoryProductSelect, cartItems, showSnackbar]);

    // Keyboard shortcut handler for telecaller role (align with Quick Order: A = inventory, Escape / = product search)
    useEffect(() => {
        if (!isTelecaller) return;

        const tryFocusProductSearchFromCart = (event) => {
            const t = event.target;
            if (!(t instanceof Element)) return;
            if (isInsideModalDialog(t)) return;
            if (t.tagName === "SELECT") return;
            if (!t.closest("[data-cart-qty-scope]") && !t.closest("[data-cart-row]")) return;
            if (!selectedCustomer?.id) return;
            event.preventDefault();
            focusProductSearchInput(true);
        };

        const handleKeyDown = (event) => {
            if (event.target.tagName === 'INPUT' ||
                event.target.tagName === 'TEXTAREA' ||
                event.target.isContentEditable) {
                return;
            }

            if (event.key === 'a' || event.key === 'A') {
                event.preventDefault();
                if (selectedCustomer?.id) {
                    const inputRect = telecallerProductSearchRef.current?.getBoundingClientRect?.();
                    if (inputRect) {
                        setInventoryModalAnchorPosition({
                            top: inputRect.bottom + 8,
                            left: inputRect.left,
                            width: inputRect.width,
                        });
                    }
                    setInventoryModalOpen(true);
                } else {
                    showSnackbar("Please select a customer first", "warning");
                }
            } else if (event.key === "Escape") {
                tryFocusProductSearchFromCart(event);
            } else if (event.key === "/" && !event.ctrlKey && !event.metaKey) {
                tryFocusProductSearchFromCart(event);
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTelecaller, selectedCustomer, showSnackbar, focusProductSearchInput]);

    const customerOptions = customerList?.customer_details || [];

    const formatPromotionDate = (dateString) => {
        if (!dateString || dateString === 'N/A') return dateString;
        try {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            if (dateString.includes('/')) {
                const [day, month, year] = dateString.split('/');
                const date = new Date(year, month - 1, day);
                return `${day}-${monthNames[date.getMonth()]}`;
            }

            if (dateString.includes('-')) {
                const date = new Date(dateString);
                if (!isNaN(date.getTime())) {
                    const day = String(date.getDate()).padStart(2, '0');
                    return `${day}-${monthNames[date.getMonth()]}`;
                }
            }

            return dateString;
        } catch (e) {
            return dateString;
        }
    };

    const getPromotionBanner = (promotion) => {
        if (promotion.type_id === "1") return "Buy X Get Y Free";
        if (promotion.type_id === "2") return "Buy X Get Y Discount";
        if (promotion.type_id === "3") return "Buy X at Discount";
        if (promotion.type_id === "4") return "Multi Layer Special";
        if (promotion.type_id === "5") return "Group Offer";
        return promotion.type || "Special Offer";
    };

    const transformPromotionData = (promotion) => {
        const buyProducts = (promotion.buy_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const offerProducts = (promotion.get_products || promotion.offer_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const allProducts = [...buyProducts, ...offerProducts];

        return {
            id: String(promotion.id),
            title: promotion.title || promotion.name || 'Promotion',
            description: promotion.description || '',
            validity: promotion.start_date && promotion.end_date
                ? `${formatPromotionDate(promotion.start_date)} to ${formatPromotionDate(promotion.end_date)}`
                : 'N/A',
            products: allProducts.map(p => p.product_name || `${p.product_code} - ${p.product_name}`),
            banner: getPromotionBanner(promotion),
            buyProducts: buyProducts,
            offerProducts: offerProducts,
            rawData: promotion
        };
    };

    const { data: cartPromotionsData, isLoading: isLoadingCartPromotions } = useQuery({
        queryKey: ['cartPromotions', customerId, promotionRefreshKey, promotionChannel],
        queryFn: () => getCartPromotions(promotionChannel, promotionChannel, customerId),
        enabled: !!customerId,
        staleTime: 0,
        gcTime: 0,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
    });

    const appliedPromotions = cartPromotionsData?.data?.applied_promotions && Array.isArray(cartPromotionsData.data.applied_promotions)
        ? cartPromotionsData.data.applied_promotions.map(transformPromotionData)
        : [];

    const applicablePromotionsFromCart = cartPromotionsData?.data?.applicable_promotions && Array.isArray(cartPromotionsData.data.applicable_promotions)
        ? cartPromotionsData.data.applicable_promotions.map(transformPromotionData)
        : [];

    const availablePromotions = applicablePromotionsFromCart.filter(promo => {
        const appliedIds = appliedPromotions.map(p => String(p.id));
        return !appliedIds.includes(String(promo.id));
    });

    const transformAPIPromotionSummary = (promo) => {
        const buyProducts = (promo.buy_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const offerProducts = (promo.get_products || promo.offer_products || []).filter(p => p.product_name && p.product_name.trim() !== '');
        const allProducts = [...buyProducts, ...offerProducts];

        const productsList = allProducts.length > 0
            ? allProducts.map(p => p.product_name || `${p.product_code || ''} - ${p.product_name || ''}`)
            : (promo.products || []);

        const validity = promo.start_date && promo.end_date
            ? `${formatPromotionDate(promo.start_date)} to ${formatPromotionDate(promo.end_date)}`
            : (promo.validity || 'N/A');

        return {
            id: String(promo.promotion_id || promo.id || ''),
            title: promo.name || promo.title || getPromotionBanner(promo),
            description: promo.description || `${promo.type || ''} - Savings: £${(promo.savings || 0).toFixed(2)}`,
            validity: validity,
            products: productsList,
            banner: getPromotionBanner(promo),
            buyProducts: buyProducts,
            offerProducts: offerProducts,
            rawData: promo,
            savings: promo.savings || 0,
            times_eligible: promo.times_eligible || 1
        };
    };

    const appliedPromotionsFromAPI = (promotionSummary?.applied_promotions || []).map(transformAPIPromotionSummary);
    const availablePromotionsFromAPI = (promotionSummary?.available_promotions || []).map(transformAPIPromotionSummary);

    const cartHasLines = cartItems.length > 0;
    const hasPromotionSummaryFromCheck = cartHasLines && promotionSummary != null;

    const reconciledPromotionLists = useMemo(() => {
        if (!cartHasLines) {
            return { applied: [], available: [], appliedCount: 0, totalApplicable: 0 };
        }
        if (hasPromotionSummaryFromCheck) {
            return reconcilePromotionDisplayLists(
                appliedPromotionsFromAPI,
                availablePromotionsFromAPI,
                cartItems
            );
        }
        return {
            applied: appliedPromotions,
            available: availablePromotions,
            appliedCount: cartPromotionsData?.data?.total_applied
                ? parseInt(cartPromotionsData.data.total_applied, 10)
                : appliedPromotions.length,
            totalApplicable: cartPromotionsData?.data?.total_applicable
                ? parseInt(cartPromotionsData.data.total_applicable, 10)
                : availablePromotions.length + appliedPromotions.length,
        };
    }, [
        cartHasLines,
        hasPromotionSummaryFromCheck,
        appliedPromotionsFromAPI,
        availablePromotionsFromAPI,
        cartItems,
        appliedPromotions,
        availablePromotions,
        cartPromotionsData,
    ]);

    const displayAppliedPromotions = reconciledPromotionLists.applied;
    const displayAvailablePromotions = reconciledPromotionLists.available;
    const promotionCount = reconciledPromotionLists.appliedCount;
    const totalApplicable = reconciledPromotionLists.totalApplicable;

    const productLinePromotionsFallback = useMemo(() => {
        if (!productLinePromotionsCtx?.lineItem) {
            return [];
        }
        const freshLineItem =
            resolveFreshCartLineForPromotionPopup(cartItems, productLinePromotionsCtx.lineItem) ??
            productLinePromotionsCtx.lineItem;
        return buildCartLinePromotionsFallback(
            freshLineItem,
            displayAppliedPromotions,
            [...displayAppliedPromotions, ...displayAvailablePromotions]
        );
    }, [productLinePromotionsCtx, cartItems, displayAppliedPromotions, displayAvailablePromotions]);

    const handleOpenPromotionsModal = () => {
        setPromotionsModalOpen(true);
    };


    const isLoadingData = isLoading;


    const handleClosePromotionsModal = () => {
        setPromotionsModalOpen(false);
    };

    const handleOpenPromotionsModalFromCartLine = useCallback(
        (lineItem) => {
            const pid = lineItem?.product_id ?? lineItem?.productId;
            if (!customerId) {
                showSnackbar("Please select a customer first", "error");
                return;
            }
            if (pid === undefined || pid === null || String(pid).trim() === "") {
                showSnackbar("Product is required", "warning");
                return;
            }
            const label =
                lineItem?.desc ||
                lineItem?.name ||
                lineItem?.product_name ||
                lineItem?.description ||
                "";
            const productIds =
                collectProductIdsSharingPromotionWithLine(cartItems, lineItem);
            const productIdList =
                productIds.length > 0 ? productIds : [String(pid)];
            setProductLinePromotionsCtx({ productId: productIdList, title: label, lineItem });
        },
        [customerId, showSnackbar, cartItems]
    );

    const handleCloseProductLinePromotions = useCallback(() => {
        setProductLinePromotionsCtx(null);
    }, []);

    const handleOpenPromotionDrawer = (promo) => {
        setSelectedPromotion(promo);
        setPromotionDrawerOpen(true);
        setPromotionsModalOpen(false);
    };

    const handleClosePromotionDrawer = () => {
        setPromotionDrawerOpen(false);
        setSelectedPromotion(null);
    };

    const handleRemovePromotion = (promo) => {
        if (!customerId) {
            showSnackbar("Please select a customer first", "error");
            return;
        }

        const promotionId = promo?.id ? Number(promo.id) : (promo?.rawData?.id ? Number(promo.rawData.id) : 0);
        if (!promotionId) return;

        const appliedIds = displayAppliedPromotions.map((p) => Number(p.id));
        const applyIds = appliedIds.filter((id) => id !== promotionId);
        const removeIds = [promotionId];

        const storageKey = `cartManualOverrides_${customerId}`;
        if (typeof window !== "undefined" && storageKey) {
            localStorage.removeItem(storageKey);
        }

        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const cartDataToUse = {
            data: cartItemsRef.current.length > 0
                ? cartItemsRef.current
                : transformProductDetailsToCart(originalOrderProducts),
            success: true
        };
        const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderIdForPayload);
        payload.promotion = { apply: applyIds, remove: removeIds };
        applyPromotionCheckTrigger(payload, PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION);

        promotionCheckEpochRef.current += 1;
        checkPromotionMutation.mutate(payload, {
            onSuccess: (result) => {
                const data = result?.data;
                const epochAtStart = result?.epochAtStart;
                if (epochAtStart !== undefined && epochAtStart !== promotionCheckEpochRef.current) {
                    return;
                }
                if (data?.success) {
                    showSnackbar("Promotion removed successfully", "success");
                } else {
                    showSnackbar(data?.message || "Failed to remove promotion", "error");
                }
            },
            onError: () => {
                showSnackbar("Failed to remove promotion. Please try again.", "error");
            }
        });
    };

    const handleCheckPromotionGetSwap = useCallback(
        async ({ promotionId, removed, newProducts }) => {
            if (!customerId) {
                return { success: false, message: "Customer is required" };
            }

            const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
            const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
            const cartDataToUse =
                cartItemsRef.current.length > 0
                    ? cartItemsRef.current
                    : transformProductDetailsToCart(originalOrderProducts);

            promotionCheckEpochRef.current += 1;
            const epochAtStart = promotionCheckEpochRef.current;
            try {
                const data = await checkPromotionWithGetProductChanges({
                    cartItems: cartDataToUse,
                    customerId,
                    channel: promotionChannel,
                    orderDate,
                    orderTime,
                    orderId: orderIdForPayload,
                    promotionId,
                    removedProducts: removed,
                    newProducts,
                    forEdit: true,
                    previewOnly: true,
                });
                if (epochAtStart !== promotionCheckEpochRef.current) {
                    return { success: false, message: "Promotion check was superseded" };
                }
                if (data?.success) {
                    const currentCartItems = cartItemsRef.current;
                    const cartDataForMerge = {
                        data:
                            currentCartItems.length > 0
                                ? currentCartItems
                                : transformProductDetailsToCart(originalOrderProducts),
                        success: true,
                    };
                    let mergedItems = mergePromotionResponseWithCart(
                        cartDataForMerge,
                        data,
                        originalOrderProducts,
                        currentCartItems
                    );
                    mergedItems = enrichCartItemsWithProductCodes(
                        mergedItems,
                        productOptionsRaw
                    );
                    cartItemsRef.current = mergedItems;
                    setCartItems(mergedItems);
                    lastValidAllotQtyRef.current = syncCartQtyValidationMap(mergedItems, {
                        qtyField: "allot",
                    });
                    if (data?.data?.totals) {
                        setApiTotals(data.data.totals);
                    }
                    if (Array.isArray(data?.data?.bundle_discounts)) {
                        setBundleDiscounts(data.data.bundle_discounts);
                    }
                    if (data?.data?.promotion_summary) {
                        setPromotionSummary(data.data.promotion_summary);
                    } else if (
                        data?.data?.applied_promotions &&
                        Array.isArray(data.data.applied_promotions)
                    ) {
                        setPromotionSummary({
                            applied_promotions: data.data.applied_promotions,
                            available_promotions:
                                data.data.available_promotions ||
                                data.data.applicable_promotions ||
                                [],
                        });
                    }
                    return { success: true };
                }
                return {
                    success: false,
                    message: data?.message || "Failed to update promotion products",
                };
            } catch (error) {
                return {
                    success: false,
                    message: error?.message || "Failed to update promotion products",
                };
            }
        },
        [
            customerId,
            orderHeader,
            promotionChannel,
            originalOrderProducts,
            productOptionsRaw,
        ]
    );

    const findPromotionForItem = (item, appliedPromotionsData) => {
        if (!item?.product_id || !appliedPromotionsData || !Array.isArray(appliedPromotionsData)) {
            return null;
        }

        const itemProductId = String(item.product_id);

        for (const promotion of appliedPromotionsData) {
            const buyProducts = promotion.buy_products || [];

            for (const buyProduct of buyProducts) {
                const productId = String(buyProduct.product_id || buyProduct.buy_product_id || buyProduct.id || "");
                if (productId && productId === itemProductId) {
                    return String(promotion.id);
                }
            }
        }

        return null;
    };

    const belongsToAppliedPromotion = (item, promo) => {
        if (!item?.product_id || !promo) {
            return false;
        }

        const itemProductId = String(item.product_id);
        const rawPromo = promo.rawData || promo;
        const buyProducts = rawPromo.buy_products || promo.buyProducts || [];

        for (const buyProduct of buyProducts) {
            const productId = String(buyProduct.product_id || buyProduct.buy_product_id || buyProduct.id || "");
            if (productId && productId === itemProductId) {
                return true;
            }
        }

        return false;
    };

    const getAppliedPromotionForCartItem = (item) => {
        if (!item || !Array.isArray(displayAppliedPromotions) || displayAppliedPromotions.length === 0) {
            return null;
        }
        const rawId = item.promotion_id ?? item.promoId ?? item.promosId ?? item.promotionId;
        const idStr = rawId != null && rawId !== "" ? String(rawId).trim() : "";
        if (idStr !== "") {
            const n = Number(rawId);
            if (Number.isFinite(n) && n > 0) {
                const found = displayAppliedPromotions.find(
                    (p) =>
                        Number(p?.id) === n ||
                        Number(p?.rawData?.id) === n ||
                        Number(p?.rawData?.promotion_id) === n
                );
                if (found) {
                    return found;
                }
            }
        }
        for (const promo of displayAppliedPromotions) {
            if (belongsToAppliedPromotion(item, promo)) {
                return promo;
            }
            const rawPromo = promo.rawData || promo;
            const getProducts =
                rawPromo.get_products ||
                rawPromo.offer_products ||
                promo.getProducts ||
                promo.offerProducts ||
                [];
            const itemPid = String(item.product_id ?? "");
            for (let i = 0; i < getProducts.length; i += 1) {
                const gp = getProducts[i];
                const pid = String(gp?.product_id ?? gp?.get_product_id ?? gp?.id ?? "");
                if (pid && pid === itemPid) {
                    return promo;
                }
            }
        }
        return null;
    };

    const getPromotionProductKeys = (p) => {
        const raw = p?.rawData || p;
        const buyProducts = raw?.buy_products || p?.buyProducts || [];
        const keys = new Set();
        buyProducts.forEach((bp) => {
            if (bp?.product_id != null) keys.add(String(bp.product_id));
            if (bp?.product_code != null && String(bp.product_code).trim() !== "") keys.add(String(bp.product_code).trim());
        });
        return keys;
    };

    const getConflictingAppliedPromotionIds = (promoBeingApplied, appliedList) => {
        const newProductKeys = getPromotionProductKeys(promoBeingApplied);
        if (newProductKeys.size === 0) return [];
        const conflictingIds = [];
        appliedList.forEach((applied) => {
            const appliedId = Number(applied.id);
            const promoId = Number(promoBeingApplied?.id ?? promoBeingApplied?.rawData?.promotion_id);
            if (appliedId === promoId) return;
            const appliedProductKeys = getPromotionProductKeys(applied);
            for (const k of appliedProductKeys) {
                if (newProductKeys.has(k)) {
                    conflictingIds.push(appliedId);
                    break;
                }
            }
        });
        return conflictingIds;
    };

    const handleApplyPromotion = async (promo) => {
        if (!customerId) {
            showSnackbar("Please select a customer first", "error");
            return false;
        }

        if (cartItems.length === 0) {
            showSnackbar("Please add products to cart first", "error");
            return false;
        }

        const newPromoId = Number(promo.id ?? promo.rawData?.id ?? promo.rawData?.promotion_id);
        const removeIds = getConflictingAppliedPromotionIds(promo, displayAppliedPromotions);
        const appliedIds = displayAppliedPromotions.map((p) => Number(p.id));
        const applyIdsAfterRemoval = appliedIds.filter((id) => !removeIds.includes(id));
        const applyIds = applyIdsAfterRemoval.includes(newPromoId)
            ? applyIdsAfterRemoval
            : [...applyIdsAfterRemoval, newPromoId];

        const storageKey = `cartManualOverrides_${customerId}`;
        if (typeof window !== "undefined" && storageKey) {
            localStorage.removeItem(storageKey);
        }

        const { date: orderDate, time: orderTime } = extractDateAndTime(orderHeader?.date);
        const orderIdForPayload = orderHeader?.order_id ?? orderHeader?.id ?? null;
        const cartDataToUse = {
            data: cartItemsRef.current.length > 0
                ? cartItemsRef.current
                : transformProductDetailsToCart(originalOrderProducts),
            success: true
        };
        const payload = transformCartToPromotionPayload(cartDataToUse, customerId, orderDate, promotionChannel, orderTime, orderIdForPayload);
        payload.promotion = { apply: applyIds, remove: removeIds };
        applyPromotionCheckTrigger(payload, PROMOTION_MESSAGE_TRIGGERS.APPLY_PROMOTION);

        promotionCheckEpochRef.current += 1;
        try {
            const result = await checkPromotionMutation.mutateAsync(payload);
            const data = result?.data;
            const epochAtStart = result?.epochAtStart;
            if (epochAtStart !== undefined && epochAtStart !== promotionCheckEpochRef.current) {
                return false;
            }
            if (data?.success) {
                showSnackbar(`Promotion "${promo.title || promo.rawData?.name || promo.rawData?.title || "Promotion"}" applied successfully`, "success");
                return true;
            }
            showSnackbar(data?.message || "Failed to apply promotion", "error");
            return false;
        } catch {
            showSnackbar("Failed to apply promotion. Please try again.", "error");
            return false;
        }
    };

    useEffect(() => {
        setProductLinePromotionsCtx((prev) => {
            if (!prev?.lineItem) {
                return prev;
            }
            const freshLineItem = resolveFreshCartLineForPromotionPopup(cartItems, prev.lineItem);
            if (!freshLineItem) {
                return prev;
            }
            return { ...prev, lineItem: freshLineItem };
        });
    }, [cartItems]);

    return (
        <>
            <Box sx={{ p: 1, backgroundColor: '#f5f7fa' }}>
                {isLoadingData && (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "300px" }}>
                        <Typography>Loading order details...</Typography>
                    </Box>
                )}
                {!isLoadingData && (
                    <Grid2 container spacing={1} sx={{ overflowX: 'hidden' }}>
                        <Grid size={{ xs: 12, md: 9 }} paper elevation={2} bgcolor="#ffffff" borderRadius={2} sx={{ px: 2, pt: 2, pb: 1, overflowX: 'hidden' }}>
                            <Grid2 container spacing={1}
                                sx={{
                                    display: "flex",
                                    flexDirection: { xs: "column", sm: "row", md: "row" },
                                    justifyContent: "space-between",
                                    alignItems: { xs: "flex-start", sm: "flex-start", md: "flex-end" },
                                }}
                            >
                                {/* <Grid2 size={{ xs: 12, md: 2 }}>
                                    <Typography sx={{ fontSize: "1.25rem", fontWeight: 600, marginBottom: "0.5rem" }}>
                                        Edit Invoice
                                    </Typography>
                                    <Typography sx={{ fontSize: "0.8rem" }}>
                                            <span style={{ fontWeight: 600 }}>Customer Name:</span> {orderHeader?.company || orderHeader?.name || ""}
                                        </Typography>
                                        <Typography sx={{ fontSize: "0.8rem" }}>
                                            <span style={{ fontWeight: 600 }}>Account No:</span> {orderHeader?.account_no || ""}
                                        </Typography>
                                </Grid2> */}
                                <Grid2 size={{ xs: 12, md: 6 }}>
                                    <CustomerSearch
                                        ref={customerSearchRef}
                                        selectedCustomer={selectedCustomer}
                                        onCustomerChange={handleCustomerChange}
                                        searchInput={customerSearchInput}
                                        onSearchInputChange={setCustomerSearchInput}
                                        options={customerOptions}
                                        isLoading={isLoadingCustomerList}
                                        debouncedSearch={debouncedCustomerSearch}
                                        onEnterKey={isTelecaller ? handleOpenInventoryModal : undefined}
                                        disabled={true}
                                        disabledTextColor="#000"
                                    />
                                </Grid2>

                                <Grid2 size={{ xs: 12, md: 6 }} display="flex" alignItems="center">
                                    <BarcodeScanner
                                        onDetected={handleBarcodeDetected}
                                        debounceInterval={600}
                                        label="Scan to add"
                                    />
                                    {isTelecaller ? (
                                        // <Button
                                        //     variant="contained"
                                        //     color="primary"
                                        //     onClick={handleOpenInventoryModal}
                                        //     disabled={!selectedCustomer?.id}
                                        //     startIcon={<Iconify icon="mdi:plus-circle" width={20} height={20} />}
                                        //     sx={{
                                        //         '&.MuiButton-contained': { height: "35px" },
                                        //         backgroundColor: "#F8991D",
                                        //         "&:hover": {
                                        //             backgroundColor: "#F8991D",
                                        //             boxShadow: "none",
                                        //             color: "#fff",
                                        //         },
                                        //     }}
                                        // >
                                        //     Add Product
                                        // </Button>
                                        <TextField
                                            className="input-border"
                                            placeholder="Enter Product Name or Code"
                                            inputRef={telecallerProductSearchRef}
                                            value={productSearchInput}
                                            onChange={handleTelecallerProductSearchChange}
                                            onKeyDown={handleTelecallerProductSearchKeyDown}
                                            size="small"
                                            disabled={!selectedCustomer?.id}
                                            sx={{ width: '100%', height: '34px' }}
                                            autoComplete="off"
                                        />
                                    ) : (
                                        <ProductSearch
                                            ref={productSearchRef}
                                            selectedProduct={selectedProduct}
                                            onProductChange={setSelectedProduct}
                                            searchInput={productSearchInput}
                                            onSearchInputChange={setProductSearchInput}
                                            options={productOptions}
                                            isLoading={isLoadingProductList}
                                            selectedCustomer={selectedCustomer}
                                            debouncedSearch={debouncedProductSearch}
                                        />
                                    )}
                                </Grid2>
                            </Grid2>

                            <Grid size={{ xs: 12, md: 12 }}>
                                <CartTable
                                    cartItems={cartItems}
                                    onTypeChange={handleValidatedTypeChange}
                                    onQuantityChange={handleValidatedQuantityChange}
                                    onPriceChange={allowEveUnitPriceEdit ? handleValidatedPriceChange : undefined}
                                    onDiscountChange={handleValidatedDiscountChange}
                                    onDeleteItem={handleDeleteItemClick}
                                    showVatAsAmount={true}
                                    onValidationError={showSnackbar}
                                    getItemDetails={getItemDetails}
                                    customerId={selectedCustomer?.id}
                                    isLoading={checkPromotionMutation.isPending}
                                    enableProductSplit={true}
                                    onAllotQtyChange={handleAllotQtyChange}
                                    onDiscountChangeComplete={handleDiscountChangeComplete}
                                    onDiscountCommentChange={isTelecaller ? handleDiscountCommentChange : null}
                                    newlyAddedProductId={newlyAddedProductId}
                                    onQuantityPopupOpen={() => {
                                        isQuantityPopupOpenRef.current = true;
                                        setNewlyAddedProductId(null);

                                    }}
                                    onQuantityPopupClose={() => {
                                        isQuantityPopupOpenRef.current = false;
                                        setNewlyAddedProductId(null);
                                        focusProductSearchInput();
                                    }}
                                    isTelecaller={isTelecaller}
                                    onPromoQtyOpenPromotionsModal={handleOpenPromotionsModalFromCartLine}
                                    onPromoQtyLineOpenDrawer={(cartItem) => {
                                        if (!cartLineHasPromoOfferQtyControls(cartItem)) return;
                                        const promo = getAppliedPromotionForCartItem(cartItem);
                                        let pid = promo?.id != null ? Number(promo.id) : 27;
                                        if (!Number.isFinite(pid) || pid <= 0) {
                                            pid = 27;
                                        }
                                        const slabId = resolveCartLineSlabId(
                                            cartItem,
                                            bundleDiscounts,
                                            cartItemsRef.current.length > 0 ? cartItemsRef.current : cartItems,
                                            promo?.rawData
                                        );
                                        setPromoProductSwapCtx({ cartItem, promotionId: pid, slabId });
                                        setPromoProductSwapOpen(true);
                                    }}
                                    bundleDiscounts={bundleDiscounts}
                                    bundleLineDiscountByKey={bundleLineDiscountByKey}
                                />
                                {/* <CartSummary
                                    cartTotals={cartTotals}
                                    totalInvDiscPercent={totalInvDiscPercent}
                                    onTotalInvDiscChange={handleValidatedTotalInvDiscChange}
                                    isEditMode={true}
                                /> */}
                                {/* <Grid container spacing={2} mt={3}>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField variant="outlined" label="Delivery Address" placeholder="Enter Delivery Address" size="small" fullWidth multiline rows={4} InputLabelProps={{ shrink: true }} value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)} />
                                    </Grid>
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <TextField variant="outlined" label="Staff Note" placeholder="Enter Staff Note" size="small" fullWidth multiline rows={4} InputLabelProps={{ shrink: true }} value={staffNote} onChange={(e) => setStaffNote(e.target.value)} />
                                    </Grid>
                                </Grid> */}
                                <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, alignItems: "center", gap: 1, justifyContent: { xs: "flex-center", md: "flex-end" }, borderTop: "1px solid #E0E0E0", pt: 1 }}>

                                    <Button
                                        variant="contained"
                                        size="medium"
                                        sx={{
                                            fontSize: "0.8rem",
                                            fontWeight: 500,
                                            '&.MuiButton-contained': { height: "32px" },
                                            width: { xs: "100%", md: "auto" },
                                            backgroundColor: "#F8991D",
                                            "&:hover": {
                                                backgroundColor: "#F8991D",
                                                boxShadow: "none",
                                                color: "#fff",
                                            },
                                        }}
                                        onClick={handleSave}>
                                        Update & Save
                                    </Button>
                                    {localStorage.getItem("user_role") === "20" && (
                                        <Button
                                            variant="contained"
                                            size="medium"

                                            sx={{
                                                fontSize: "0.8rem",
                                                fontWeight: 500,
                                                '&.MuiButton-contained': { height: "32px" },
                                                width: { xs: "100%", md: "auto" },
                                                backgroundColor: "#F8991D",
                                                "&:hover": {
                                                    backgroundColor: "#F8991D",
                                                    boxShadow: "none",
                                                    color: "#fff",
                                                },
                                            }}
                                            onClick={handlePrintInvoice}
                                        >
                                            Print & Dispatch
                                        </Button>
                                    )}
                                    {/* {isRhp ? (
                                        <>
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: 500,
                                                    '&.MuiButton-contained': { height: "32px" },
                                                    width: { xs: "100%", md: "auto" },
                                                    backgroundColor: "#F8991D",
                                                    "&:hover": {
                                                        backgroundColor: "#F8991D",
                                                        boxShadow: "none",
                                                    },
                                                }}
                                                onClick={() => handlePrintDeliveryNote(orderId)}>
                                                Print Delivery Note
                                            </Button>
                                            <Button
                                                variant="contained"
                                                size="medium"
                                                sx={{
                                                    fontSize: "0.8rem",
                                                    fontWeight: 500,
                                                    '&.MuiButton-contained': { height: "32px" },
                                                    width: { xs: "100%", md: "auto" },
                                                    backgroundColor: "#F8991D",
                                                    "&:hover": {
                                                        backgroundColor: "#F8991D",
                                                        boxShadow: "none",
                                                        color: "#fff",
                                                    },
                                                }}
                                                onClick={() => handlePrintDrinkSheet(orderId)}>
                                                Print Drink Sheet
                                            </Button>
                                        </>)
                                        : null} */}
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid2 size={{ xs: 12, md: 3 }} paper elevation={2} bgcolor="#ffffff" borderRadius={2} sx={{ p: 2, overflowX: 'hidden' }}>
                            <CartSummaryEposSales
                                cartTotals={cartTotals}
                                totalInvDiscPercent={totalInvDiscPercent}
                                onTotalInvDiscChange={handleValidatedTotalInvDiscChange}
                                isEditMode={true}
                                isTelecaller={isTelecaller}
                            />

                            <Paper
                                variant="elevation"
                                elevation={0}
                                sx={{
                                    px: { xs: 1, sm: 1.5 },
                                    py: { xs: 1, sm: 1.5 },
                                    mt: { xs: 1.5, sm: 2 },
                                    mb: { xs: 3, sm: 0 },
                                    backgroundColor: '#E3F2FD',
                                    borderRadius: '10px',
                                    border: 'none'
                                }}
                            >
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 1, sm: 2 }}>
                                    <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center" sx={{ flex: 1 }}>
                                        <Iconify
                                            icon="ri:discount-percent-line"
                                            width={32}
                                            height={32}
                                            sx={{ color: '#F8991D', width: { xs: 28, sm: 32 }, height: { xs: 28, sm: 32 } }}
                                        />
                                        <Typography
                                            fontWeight={600}
                                            sx={{ color: '#000', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                        >
                                            {promotionCount > 0
                                                ? `${promotionCount} out of ${totalApplicable} promotion${totalApplicable !== 1 ? 's' : ''} applied!`
                                                : totalApplicable > 0
                                                    ? `${totalApplicable} promotion${totalApplicable !== 1 ? 's' : ''} available to apply!`
                                                    : '0 promotions available'
                                            }
                                        </Typography>
                                    </Stack>
                                    <Button
                                        variant="contained"
                                        size="small"
                                        onClick={handleOpenPromotionsModal}
                                        fullWidth={false}
                                        sx={{
                                            backgroundColor: '#F8991D',
                                            color: '#fff',
                                            borderRadius: '6px',
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            px: { xs: 1.5, sm: 2 },
                                            py: { xs: 0.75, sm: 1 },
                                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                            alignSelf: { xs: 'stretch', sm: 'auto' },
                                            '&:hover': {
                                                backgroundColor: '#F8991D'
                                            }
                                        }}
                                    >
                                        View
                                    </Button>
                                </Stack>
                            </Paper>
                        </Grid2>
                    </Grid2>
                )}
            </Box>

            <PromotionsDialog
                open={promotionsModalOpen}
                onClose={handleClosePromotionsModal}
                appliedPromotions={displayAppliedPromotions}
                availablePromotions={displayAvailablePromotions}
                isLoading={isLoadingCartPromotions}
                totalApplicable={totalApplicable}
                onOpenDrawer={handleOpenPromotionDrawer}
                onRemovePromotion={handleRemovePromotion}
                onApplyPromotion={handleApplyPromotion}
            />

            <ProductLinePromotionsDialog
                open={Boolean(productLinePromotionsCtx)}
                onClose={handleCloseProductLinePromotions}
                productId={productLinePromotionsCtx?.productId ?? ""}
                customerId={customerId}
                orderId={orderId}
                productTitle={productLinePromotionsCtx?.title ?? ""}
                platform={promotionChannel}
                channels={promotionChannel}
                cartItems={cartItems}
                lineItem={productLinePromotionsCtx?.lineItem ?? null}
                orderDate={extractDateAndTime(orderHeader?.date).date}
                orderTime={extractDateAndTime(orderHeader?.date).time}
                forEdit
                fallbackPromotions={productLinePromotionsFallback}
                onApplyPromotion={handleApplyPromotion}
            />

            {isTelecaller && (
                <InventorySearchModal
                    open={inventoryModalOpen}
                    onClose={handleCloseInventoryModal}
                    onProductSelect={handleInventoryProductSelectForQuantity}
                    productOptions={productOptions}
                    isLoading={isLoadingProductList}
                    productSearchInput={productSearchInput}
                    selectedCustomer={selectedCustomer}
                    anchorPosition={inventoryModalAnchorPosition}
                    onFocusOuterProductSearch={() => focusProductSearchInput(true)}
                />
            )}

            <NumericKeypad
                open={quantityKeypadOpen}
                onClose={() => {
                    setQuantityKeypadOpen(false);
                    setSelectedProductForQuantity(null);
                    setKeypadError("");
                    focusProductSearchInput();
                }}
                onConfirm={(val, orderTypeFromKeypad) => {
                    const product = selectedProductForQuantity;
                    if (!product || !selectedCustomer?.id) return;
                    const normalizeOrderType = (v) => {
                        const raw = String(v ?? "Box").trim().toLowerCase();
                        if (raw === "piece" || raw === "p" || raw === "1") return "Piece";
                        if (raw === "box" || raw === "b" || raw === "0") return "Box";
                        return raw.startsWith("p") ? "Piece" : "Box";
                    };
                    const orderType = normalizeOrderType(orderTypeFromKeypad || keypadInitialOrderType);
                    const qtyToAdd = parseFloat(val);
                    if (!Number.isFinite(qtyToAdd) || qtyToAdd <= 0) return;

                    const pid = String(product.id || product.product_id || "");
                    const existing = findMergeableCartLineForProductAdd(
                        cartItemsRef.current,
                        pid,
                        orderType
                    );
                    const existingQty = existing ? (parseFloat(existing.qty) || 0) : 0;
                    const maxQty = isMeatsProduct(product) ? 9999.99 : 9999;
                    if (existingQty + qtyToAdd > maxQty) {
                        setKeypadError(`Quantity cannot exceed ${maxQty}`);
                        return;
                    }

                    const paidCatalogProduct = {
                        ...product,
                        id: pid,
                        product_id: pid,
                        productId: pid,
                        order_type: orderType,
                        is_promoted: "0",
                        promo_type: "",
                        promoType: "",
                        promosType: "",
                        generated: false,
                        free_product: "N",
                        promotion_applied: false,
                    };
                    const mergedForCap = existing
                        ? { ...existing, order_type: orderType }
                        : mergeOrderLimitFieldsFromCartLines(
                              paidCatalogProduct,
                              cartItemsRef.current
                          );
                    const targetTotal = existing ? existingQty + qtyToAdd : qtyToAdd;
                    const allowDec =
                        isMeatsProduct(product) || (existing && isMeatsProduct(existing));
                    const purchaseLimitKeypad = existing
                        ? validateMaximumOrderQuantityForLineQty(
                              mergedForCap,
                              cartItemsRef.current,
                              targetTotal,
                              { qtyField: "allot" }
                          )
                        : validateMaximumOrderQuantityForCartAdd(
                              mergedForCap,
                              cartItemsRef.current,
                              qtyToAdd,
                              { qtyField: "allot" }
                          );
                    if (!purchaseLimitKeypad.allowed) {
                        showPurchaseLimitPopup(purchaseLimitKeypad.message, mergedForCap);
                        return;
                    }
                    if (shouldCheckStockBeforeAddToCart) {
                        const lineForStock = existing?.originalItem || existing || mergedForCap;
                        const lineKey = existing
                            ? getCartLineValidationKey(existing)
                            : getCartLineValidationKey({
                                  ...mergedForCap,
                                  product_id: pid,
                                  order_type: orderType,
                              });
                        const stockQtyToCheck = getProductQtyTotalInCart(
                            cartItemsRef.current,
                            lineForStock,
                            "allot",
                            lineKey,
                            targetTotal
                        );
                        if (!isQuantityWithinLineStock(lineForStock, stockQtyToCheck, allowDec)) {
                            const cap = getStockCapForCartLineItem(lineForStock);
                            showStockLimitPopup(lineForStock, cap != null ? Math.floor(cap) : 0);
                            return;
                        }
                    }

                    const productWithQty = {
                        ...product,
                        quantity: qtyToAdd,
                        qty: String(qtyToAdd),
                        order_type: orderType,
                        type: orderType
                    };
                    setQuantityKeypadOpen(false);
                    setSelectedProductForQuantity(null);
                    setKeypadError("");
                    handleInventoryProductSelect(productWithQty, { skipQuantityPopup: true });
                }}
                title="Enter Quantity"
                error={keypadError}
                initialValue=""
                min={isMeatsProduct(selectedProductForQuantity) ? 0.01 : 1}
                max={isMeatsProduct(selectedProductForQuantity) ? 9999.99 : 9999}
                canSplit={parseInt(selectedProductForQuantity?.split ?? selectedProductForQuantity?.noOfPieceInBox ?? 0, 10) === 2}
                initialOrderType={keypadInitialOrderType}
                allowDecimal={isMeatsProduct(selectedProductForQuantity)}
            />

            <PromotionDetailsDrawerEditEpos
                open={promotionDrawerOpen}
                onClose={handleClosePromotionDrawer}
                promotion={selectedPromotion}
                cartItems={cartItems}
                customerId={customerId}
                orderId={orderId}
                onCheckPromotionGetSwap={handleCheckPromotionGetSwap}
                onCartUpdate={(updatedItems, updatedTotals) => {
                    promotionCheckEpochRef.current += 1;
                    const nextItems = Array.isArray(updatedItems)
                        ? updatedItems.map((item) => {
                            const raw =
                                item.allot_qty !== undefined && item.allot_qty !== null && item.allot_qty !== ''
                                    ? item.allot_qty
                                    : item.qty !== undefined && item.qty !== null && item.qty !== ''
                                        ? item.qty
                                        : item.quantity;
                            const num = parseFloat(raw);
                            const n = Number.isFinite(num) ? num : (parseInt(String(raw), 10) || 0);
                            const str = String(n);
                            return { ...item, quantity: n, qty: str, allot_qty: str };
                        })
                        : updatedItems;
                    cartItemsRef.current = nextItems;
                    setCartItems(nextItems);
                    if (updatedTotals) {
                        setApiTotals(updatedTotals);
                    }
                }}
                onQueuePromotionCheck={(queueData) => {
                    const entries = Object.entries(queueData || {});
                    if (!entries.length) {
                        enqueuePromotionCheck(null, null);
                        return;
                    }
                    entries.forEach(([pid, meta]) => {
                        const trigger =
                            meta && typeof meta === "object"
                                ? meta.trigger || null
                                : null;
                        enqueuePromotionCheck(pid, trigger);
                    });
                }}
            />

            <PromotionGetProductsDialog
                open={promoProductSwapOpen}
                onClose={() => {
                    setPromoProductSwapOpen(false);
                    setPromoProductSwapCtx(null);
                }}
                cartItem={promoProductSwapCtx?.cartItem ?? null}
                promotionId={promoProductSwapCtx?.promotionId ?? 27}
                slabId={promoProductSwapCtx?.slabId ?? null}
                channels={5}
                platform={localStorage.getItem("user_role") === "20" ? "5" : "4"}
                customerId={customerId}
                orderId={orderId}
                localCartItems={cartItems}
                bundleDiscounts={bundleDiscounts}
                onCheckPromotionGetSwap={handleCheckPromotionGetSwap}
                onSwapSuccess={() => {
                    setSnackbar({
                        open: true,
                        message: "Promotion product updated",
                        severity: "success",
                    });
                }}
            />

            <ConfirmDialog
                open={saveConfirmDialog}
                onClose={handleCloseSaveConfirmDialog}
                title="Confirm Order"
                content={
                    <Typography>
                        Do you really want to submit this order?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        onClick={handleConfirmSave}
                        disabled={updateInvoiceMutation.isPending}
                        sx={{
                            backgroundColor: '#F8991D',
                            color: '#fff',
                            ml: 1,
                            '&:hover': {
                                backgroundColor: '#F8991D',
                                color: '#fff'
                            }
                        }}
                    >
                        {updateInvoiceMutation.isPending ? "Submitting..." : "Confirm"}
                    </Button>
                }
            />

            <ConfirmDialog
                open={cancelConfirmDialog}
                onClose={handleCloseCancelConfirmDialog}
                title="Discard Changes"
                content={
                    <Typography>
                        Do you really want to cancel and discard all changes?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmCancel}
                        sx={{ ml: 1 }}
                    >
                        Discard
                    </Button>
                }
            />

            <ConfirmDialog
                open={saveAndInvoiceConfirmDialog}
                onClose={handleCloseSaveAndInvoiceConfirmDialog}
                title="Save & Invoice"
                content={
                    <Typography>
                        This action will move the order to Delivered state. Do you want to continue?
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        onClick={handleConfirmSaveAndInvoice}
                        sx={{
                            backgroundColor: '#F8991D',
                            color: '#fff',
                            ml: 1,
                            '&:hover': {
                                backgroundColor: '#F8991D',
                                color: '#fff'
                            }
                        }}
                    >
                        Confirm
                    </Button>
                }
            />

            <ConfirmDialog
                open={deleteItemDialog.open}
                onClose={handleCloseDeleteItemDialog}
                title="Delete Item"
                content="Do you really want to delete this item?"
                action={
                    <Button
                        variant="contained"
                        onClick={handleConfirmDeleteItem}
                        sx={{
                            ml: 1,
                            backgroundColor: '#F8991D',
                            color: '#fff',
                            '&:hover': {
                                backgroundColor: '#F8991D',
                                color: '#fff'
                            }
                        }}
                    >
                        Delete
                    </Button>
                }
            />

            <SalesStockLimitDialog
                open={telecallerStockDialog.open}
                onClose={handleCloseTelecallerStockDialog}
                productName={telecallerStockDialog.productName}
                productCode={telecallerStockDialog.productCode}
                maxQty={telecallerStockDialog.maxQty}
                message={telecallerStockDialog.message || ""}
                plainTitle={telecallerStockDialog.plainTitle}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                    transition: {
                        style: { transformOrigin: 'top right' },
                    },
                }}
                sx={(theme) => ({
                    zIndex: theme.zIndex.snackbar,
                    top: 24,
                    right: 24,
                    left: 'auto',
                    bottom: 'auto',
                })}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled" sx={{ width: '100%', maxWidth: 420 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default EditEposSales;

